import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";

import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Paper,
  Chip,
  Divider,
  Stack,
  IconButton,
  Avatar,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  FormControlLabel,
  Switch,
} from "@mui/material";
import {
  ChevronLeftRounded,
  PersonRounded,
  AssignmentRounded,
  MonitorHeartRounded,
  LocationOnRounded,
  CakeRounded,
  WcRounded,
  MedicationRounded,
  CoronavirusRounded,
  MedicalServicesRounded,
  FactCheckRounded,
  VaccinesRounded,
  AssessmentRounded,
  DescriptionRounded,
  HistoryEduRounded,
  SearchRounded,
  TranslateRounded,
  SecurityRounded,
  CallRounded,
  MailRounded,
  RocketLaunchRounded,
  UpdateRounded,
  GavelRounded,
  RuleRounded,
  InfoOutlined,
  ElderlyRounded,
  LocalHospitalRounded,
  ScienceRounded,
} from "@mui/icons-material";
import { api } from "../utils/api";
import { PCC_AUTH_URL } from "../constants/pcc";
import {
  FALL_HISTORY_ICD_CODES,
  FALL_KEYWORDS,
  CODE_STATUS_KEYWORDS,
  SURGICAL_CPT_MIN,
  SURGICAL_CPT_MAX,
  WOUND_FIELD_LABELS,
} from "../constants/clinicalDetection";
import Table, { TableHeaderConfig, StatusDot } from "../components/Table";

const tabs = [
  { id: "details", label: "Patient Details", icon: <PersonRounded /> },
  { id: "encounters", label: "Encounters", icon: <HistoryEduRounded /> },
  { id: "conditions", label: "Conditions", icon: <AssignmentRounded /> },
  { id: "fall-history", label: "Fall History", icon: <ElderlyRounded /> },
  { id: "vitals", label: "Vital Signs", icon: <MonitorHeartRounded /> },
  { id: "labs", label: "Labs & Results", icon: <ScienceRounded /> },
  { id: "observations", label: "Observations", icon: <MonitorHeartRounded /> },
  { id: "medications", label: "Medications", icon: <MedicationRounded /> },
  { id: "allergies", label: "Allergies", icon: <CoronavirusRounded /> },
  { id: "procedures", label: "Procedures", icon: <MedicalServicesRounded /> },
  {
    id: "surgical-history",
    label: "Surgical History",
    icon: <LocalHospitalRounded />,
  },
  { id: "careplans", label: "Care Plans", icon: <FactCheckRounded /> },
  { id: "immunizations", label: "Immunizations", icon: <VaccinesRounded /> },
  { id: "reports", label: "Diagnostic Reports", icon: <AssessmentRounded /> },
  { id: "documents", label: "Documents", icon: <DescriptionRounded /> },
  {
    id: "advanced-directives",
    label: "Advanced Directives",
    icon: <GavelRounded />,
  },
  { id: "code-status", label: "Code Status", icon: <GavelRounded /> },
  { id: "evaluations", label: "Evaluations", icon: <RuleRounded /> },
];

const VITAL_SIGN_GROUPS: Record<string, string[]> = {
  "Blood Pressure": ["blood pressure", "systolic", "diastolic"],
  "Heart Rate": ["heart rate", "pulse"],
  Temperature: ["temperature", "temp"],
  SpO2: ["oxygen saturation", "spo2", "pulse oximetry"],
  "Respiratory Rate": ["respiratory rate"],
  Height: ["height", "body height"],
  Weight: ["weight", "body weight"],
};

const ADVANCED_DIRECTIVES_KEYWORD = "Advanced Directives";
const EVALUATIONS_KEYWORD = "Evaluations";

const highlightText = (text: string, highlight: string) => {
  if (!highlight.trim()) {
    return <span>{text}</span>;
  }
  const escaped = highlight.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const regex = new RegExp(`(${escaped})`, "gi");
  const parts = text.split(regex);
  return (
    <>
      {parts.map((part, i) =>
        // With a single capture group, split() places the captured matches
        // at odd indices — using a stateful global regex.test() here instead
        // would drift via lastIndex and highlight the wrong parts.
        i % 2 === 1 ? (
          <span
            key={i}
            style={{
              backgroundColor: "rgba(253, 224, 71, 0.4)",
              color: "#1e293b",
              fontWeight: 700,
              borderRadius: "2px",
              padding: "0 2px",
            }}
          >
            {part}
          </span>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </>
  );
};

// Minimum number of characters required before a search query is applied
const MIN_SEARCH_LENGTH = 3;

// Levenshtein distance, used to allow small typos in fuzzy search
const levenshteinDistance = (a: string, b: string) => {
  const matrix: number[][] = Array.from({ length: a.length + 1 }, (_, i) => [
    i,
    ...Array(b.length).fill(0),
  ]);
  for (let j = 0; j <= b.length; j++) matrix[0][j] = j;

  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      matrix[i][j] =
        a[i - 1] === b[j - 1]
          ? matrix[i - 1][j - 1]
          : 1 +
            Math.min(
              matrix[i - 1][j - 1],
              matrix[i][j - 1],
              matrix[i - 1][j],
            );
    }
  }
  return matrix[a.length][b.length];
};

// Fuzzy match: true if `text` contains `query` as a substring, or contains a
// word that is a close (typo-tolerant) match for `query`
const fuzzyMatch = (text: string, query: string) => {
  if (!text || !query) return false;
  if (text.includes(query)) return true;

  const maxDistance = query.length <= 4 ? 1 : 2;
  const words = text.split(/[\s,/.-]+/).filter(Boolean);
  return words.some((word) => {
    const slice = word.slice(0, query.length + maxDistance);
    return levenshteinDistance(slice, query) <= maxDistance;
  });
};

// Helper functions for FHIR Patient Resource parsing
const getExtensionValue = (patient: any, url: string) => {
  return patient?.extension?.find((ext: any) => ext.url === url);
};

const getBirthsex = (patient: any) => {
  const ext = getExtensionValue(
    patient,
    "http://hl7.org/fhir/us/core/StructureDefinition/us-core-birthsex",
  );
  return ext?.valueCode || null;
};

const getGenderIdentity = (patient: any) => {
  const ext = getExtensionValue(
    patient,
    "http://hl7.org/fhir/us/core/StructureDefinition/us-core-genderIdentity",
  );
  return (
    ext?.valueCodeableConcept?.text ||
    ext?.valueCodeableConcept?.coding?.[0]?.display ||
    null
  );
};

const getRace = (patient: any) => {
  const ext = getExtensionValue(
    patient,
    "http://hl7.org/fhir/us/core/StructureDefinition/us-core-race",
  );
  if (!ext) return null;
  const textSub = ext.extension?.find((sub: any) => sub.url === "text");
  if (textSub?.valueString) return textSub.valueString;
  const ombSub = ext.extension?.find((sub: any) => sub.url === "ombCategory");
  return ombSub?.valueCoding?.display || ombSub?.valueCoding?.code || null;
};

const getAge = (birthDateString: string) => {
  if (!birthDateString) return null;
  const today = new Date();
  const birthDate = new Date(birthDateString);
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

// Returns a human-friendly relative time string (e.g. "2 days ago")
const getRelativeTime = (dateString: string) => {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return null;

  const diffMs = Date.now() - date.getTime();
  const diffSec = Math.round(diffMs / 1000);
  const diffMin = Math.round(diffSec / 60);
  const diffHour = Math.round(diffMin / 60);
  const diffDay = Math.round(diffHour / 24);

  if (diffSec < 60) return "just now";
  if (diffMin < 60) return `${diffMin} minute${diffMin === 1 ? "" : "s"} ago`;
  if (diffHour < 24) return `${diffHour} hour${diffHour === 1 ? "" : "s"} ago`;
  if (diffDay < 30) return `${diffDay} day${diffDay === 1 ? "" : "s"} ago`;

  return date.toLocaleDateString();
};

// Formats a date in UTC as "YYYY-MM-DD HH:mm UTC", so the timestamp is
// unambiguous and consistent for users in any timezone
const formatUtc = (dateString: string) => {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "Unknown";

  const pad = (n: number) => String(n).padStart(2, "0");
  const year = date.getUTCFullYear();
  const month = pad(date.getUTCMonth() + 1);
  const day = pad(date.getUTCDate());
  const hours = pad(date.getUTCHours());
  const minutes = pad(date.getUTCMinutes());

  return `${year}-${month}-${day} ${hours}:${minutes} UTC`;
};

const PatientDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [patientData, setPatientData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDocOpen, setIsDocOpen] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState<any>(null);
  const [selectedRecordIndex, setSelectedRecordIndex] = useState<number | null>(
    null,
  );
  const [activeTab, setActiveTab] = useState(0);
  const [localSearch, setLocalSearch] = useState("");
  const [globalSearch, setGlobalSearch] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [clinicalStatusFilter, setClinicalStatusFilter] = useState("all");
  const [verificationStatusFilter, setVerificationStatusFilter] =
    useState("all");
  const [medicationStatusFilter, setMedicationStatusFilter] = useState("all");
  const [medicationIntentFilter, setMedicationIntentFilter] = useState("all");
  const [allergyStatusFilter, setAllergyStatusFilter] = useState("all");
  const [allergyCriticalityFilter, setAllergyCriticalityFilter] =
    useState("all");
  const [procedureStatusFilter, setProcedureStatusFilter] = useState("all");
  const [immunizationStatusFilter, setImmunizationStatusFilter] =
    useState("all");
  const [documentStatusFilter, setDocumentStatusFilter] = useState("all");
  const [documentTypeFilter, setDocumentTypeFilter] = useState("all");
  const [hospiceDialogOpen, setHospiceDialogOpen] = useState(false);
  const [codeStatusDialogOpen, setCodeStatusDialogOpen] = useState(false);
  const [selectedProcedure, setSelectedProcedure] = useState<any>(null);
  const [isProcedureOpen, setIsProcedureOpen] = useState(false);
  const [vitalsLatestOnly, setVitalsLatestOnly] = useState(false);
  const activeSection = tabs[activeTab]?.id;
  const contentScrollRef = useRef<HTMLDivElement>(null);
  const isProgrammaticNav = useRef(false);

  useEffect(() => {
    if (contentScrollRef.current) {
      contentScrollRef.current.scrollTop = 0;
    }
    if (isProgrammaticNav.current) {
      isProgrammaticNav.current = false;
      return;
    }
    setLocalSearch("");
    setClinicalStatusFilter("all");
    setVerificationStatusFilter("all");
    setMedicationStatusFilter("all");
    setMedicationIntentFilter("all");
    setAllergyStatusFilter("all");
    setAllergyCriticalityFilter("all");
    setProcedureStatusFilter("all");
    setImmunizationStatusFilter("all");
    setDocumentStatusFilter("all");
    setDocumentTypeFilter("all");
    setVitalsLatestOnly(false);
  }, [activeTab]);

  const fetchPatientDetail = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const res = await api.get(`/pcc/patients/${id}`);
      setPatientData(res.data);
    } catch (err: any) {
      console.error("Error fetching patient detail:", err);
      setError(err.message || "Failed to fetch patient details");
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      void fetchPatientDetail();
    }
  }, [id, fetchPatientDetail]);

  // Data extraction and parsing
  const isMultiple = Array.isArray(patientData);
  const record = isMultiple
    ? selectedRecordIndex !== null
      ? patientData[selectedRecordIndex]
      : patientData.find(
          (p: any) =>
            p.patient_id === id || p.id === id || p.details?.patient?.id === id,
        ) || patientData[0]
    : patientData;

  const details = record?.details || record || {};

  // Prefer updatedAt: it's a real UTC timestamp, whereas `date` is a
  // pre-formatted Chicago-time string with no timezone info (misparsed
  // as local time by `new Date()`).
  const lastFetchedAt = record?.updatedAt || record?.date || null;

  const flattenBundle = useCallback((resource: any) => {
    if (resource?.resourceType === "Bundle" && Array.isArray(resource.entry)) {
      return resource.entry.map((e: any) => e.resource).filter(Boolean);
    }
    return Array.isArray(resource) ? resource : [];
  }, []);

  const Encounters = useMemo(() => {
    return Array.isArray(record?.encounterLevelData)
      ? record.encounterLevelData
      : [];
  }, [record?.encounterLevelData]);

  const Condition = useMemo(
    () => flattenBundle(details.conditions),
    [flattenBundle, details.conditions],
  );
  const Observation = useMemo(
    () => flattenBundle(details.observations),
    [flattenBundle, details.observations],
  );
  const MedicationRequest = useMemo(() => {
    return Array.isArray(details.medications) ? details.medications : [];
  }, [details.medications]);
  const medicationDetails = useMemo(() => {
    return Array.isArray(details.medicationDetails)
      ? details.medicationDetails
      : [];
  }, [details.medicationDetails]);
  const alergy = useMemo(() => {
    return Array.isArray(details.allergies) ? details.allergies : [];
  }, [details.allergies]);
  const procedure = useMemo(() => {
    return Array.isArray(details.procedures) ? details.procedures : [];
  }, [details.procedures]);
  const carePlan = useMemo(() => {
    return Array.isArray(details.carePlans) ? details.carePlans : [];
  }, [details.carePlans]);
  const immunization = useMemo(() => {
    return Array.isArray(details.immunizations) ? details.immunizations : [];
  }, [details.immunizations]);
  const DiagnosticReport = useMemo(() => {
    return Array.isArray(details.diagnosticReports)
      ? details.diagnosticReports
      : [];
  }, [details.diagnosticReports]);
  const documents = useMemo(() => {
    return Array.isArray(details.documents) ? details.documents : [];
  }, [details.documents]);
  const practitioners = useMemo(
    () => flattenBundle(details.practitioners),
    [flattenBundle, details.practitioners],
  );
  const Patient = details.patient;

  const name = Patient?.name?.[0];
  const fullName = Patient
    ? `${name?.given?.join(" ")} ${name?.family}`
    : "Unknown Patient";
  const address = Patient?.address?.[0];
  const fullAddress = address
    ? `${address?.line?.join(", ")}, ${address?.city}, ${address?.state} ${address?.postalCode}`
    : "N/A";
console.log("procedure",procedure);

  const getMedicationName = useCallback(
    (m: any) => {
      if (m.medicationCodeableConcept?.text)
        return m.medicationCodeableConcept.text;
      const ref = m.medicationReference?.reference;
      if (ref) {
        const id = ref.split("/").pop();
        const detail = medicationDetails?.find((d: any) => d.id === id);
        if (detail?.code?.text) return detail.code.text;
        if (detail?.code?.coding?.[0]?.display)
          return detail.code.coding[0].display;
      }
      return m.medicationReference?.display || "Unknown Medication";
    },
    [medicationDetails],
  );

  const getPractitionerDetails = (ref: string) => {
    if (!ref) return null;
    const practId = ref.split("/").pop();
    const p = practitioners.find((pract: any) => pract.id === practId);
    if (!p) return null;

    const nameObj = p.name?.[0];
    const fullName = nameObj
      ? `${nameObj.given?.join(" ")} ${nameObj.family}`
      : "Unknown Practitioner";

    const telecom = p.telecom
      ?.map((t: any) => `${t.system}${t.use ? ` (${t.use})` : ""}: ${t.value}`)
      .join(", ");

    return { fullName, telecom };
  };

  // Filtered arrays based on local search
  const filteredEncounters = useMemo(() => {
    const sorted = [...Encounters].sort(
      (a: any, b: any) =>
        new Date(b.start || b.period?.start || 0).getTime() -
        new Date(a.start || a.period?.start || 0).getTime(),
    );
    if (!localSearch) return sorted;
    const query = localSearch.toLowerCase().trim();
    return sorted.filter((enc: any) => {
      const typeDisplay = (
        enc.type?.[0]?.coding?.[0]?.display || "Encounter"
      ).toLowerCase();
      const status = (enc.status || "").toLowerCase();
      const conditionMatches = enc.conditions?.some((c: any) =>
        (c.code?.coding?.[0]?.display || c.code?.text || "")
          .toLowerCase()
          .includes(query),
      );
      const observationMatches = enc.observations?.some((o: any) =>
        (o.code?.coding?.[0]?.display || "").toLowerCase().includes(query),
      );
      return (
        typeDisplay.includes(query) ||
        status.includes(query) ||
        conditionMatches ||
        observationMatches
      );
    });
  }, [Encounters, localSearch]);

  const filteredConditions = useMemo(() => {
    let result = [...Condition].sort(
      (a: any, b: any) =>
        new Date(b.recordedDate || b.onsetDateTime || 0).getTime() -
        new Date(a.recordedDate || a.onsetDateTime || 0).getTime(),
    );

    if (clinicalStatusFilter !== "all") {
      result = result.filter(
        (c: any) =>
          (c.clinicalStatus?.coding?.[0]?.code || "").toLowerCase() ===
          clinicalStatusFilter.toLowerCase(),
      );
    }

    if (verificationStatusFilter !== "all") {
      result = result.filter(
        (c: any) =>
          (c.verificationStatus?.coding?.[0]?.code || "").toLowerCase() ===
          verificationStatusFilter.toLowerCase(),
      );
    }

    const query = localSearch.toLowerCase().trim();
    if (query.length < MIN_SEARCH_LENGTH) return result;
    return result.filter((c: any) => {
      const name = (
        c.code?.text ||
        c.code?.coding?.[0]?.display ||
        ""
      ).toLowerCase();
      const status = (c.clinicalStatus?.coding?.[0]?.code || "").toLowerCase();
      const verification = (
        c.verificationStatus?.coding?.[0]?.code || ""
      ).toLowerCase();
      const category = (
        c.category?.[0]?.coding?.[0]?.display || ""
      ).toLowerCase();
      const date = c.recordedDate
        ? new Date(c.recordedDate).toLocaleDateString().toLowerCase()
        : "";
      const icdCode = (c.code?.coding || [])
        .map((cd: any) => cd.code || "")
        .join(" ")
        .toLowerCase();
      return (
        fuzzyMatch(name, query) ||
        fuzzyMatch(status, query) ||
        fuzzyMatch(verification, query) ||
        fuzzyMatch(category, query) ||
        date.includes(query) ||
        fuzzyMatch(icdCode, query)
      );
    });
  }, [Condition, localSearch, clinicalStatusFilter, verificationStatusFilter]);

  const filteredObservations = useMemo(() => {
    const sorted = [...Observation].sort(
      (a: any, b: any) =>
        new Date(b.effectiveDateTime || b.issued || 0).getTime() -
        new Date(a.effectiveDateTime || a.issued || 0).getTime(),
    );
    if (!localSearch) return sorted;
    const query = localSearch.toLowerCase().trim();
    return sorted.filter((o: any) => {
      const name = (
        o.code?.text ||
        o.code?.coding?.[0]?.display ||
        ""
      ).toLowerCase();
      const category = (
        o.category?.[0]?.coding?.[0]?.display || ""
      ).toLowerCase();
      const val = o.valueQuantity?.value
        ? String(o.valueQuantity.value).toLowerCase()
        : "";
      const unit = (o.valueQuantity?.unit || "").toLowerCase();
      return (
        name.includes(query) ||
        category.includes(query) ||
        val.includes(query) ||
        unit.includes(query)
      );
    });
  }, [Observation, localSearch]);

  const filteredMedications = useMemo(() => {
    let result = [...MedicationRequest].sort(
      (a: any, b: any) =>
        new Date(b.authoredOn || 0).getTime() -
        new Date(a.authoredOn || 0).getTime(),
    );

    if (medicationStatusFilter === "inactive") {
      result = result.filter((m: any) =>
        ["stopped", "completed"].includes((m.status || "").toLowerCase())
      );
    } else if (medicationStatusFilter === "active") {
      result = result.filter((m: any) =>
        !["stopped", "completed"].includes((m.status || "").toLowerCase())
      );
    }

    if (medicationIntentFilter !== "all") {
      result = result.filter(
        (m: any) =>
          (m.intent || "").toLowerCase() ===
          medicationIntentFilter.toLowerCase(),
      );
    }

    if (!localSearch) return result;
    const query = localSearch.toLowerCase().trim();
    return result.filter((m: any) => {
      const name = getMedicationName(m).toLowerCase();
      const status = (m.status || "").toLowerCase();
      const intent = (m.intent || "").toLowerCase();
      const dosage = (m.dosageInstruction?.[0]?.text || "").toLowerCase();
      return (
        name.includes(query) ||
        status.includes(query) ||
        intent.includes(query) ||
        dosage.includes(query)
      );
    });
  }, [
    MedicationRequest,
    localSearch,
    medicationStatusFilter,
    medicationIntentFilter,
    getMedicationName,
  ]);

  const filteredAllergies = useMemo(() => {
    let result = [...alergy].sort(
      (a: any, b: any) =>
        new Date(b.recordedDate || b.onsetDateTime || 0).getTime() -
        new Date(a.recordedDate || a.onsetDateTime || 0).getTime(),
    );

    if (allergyStatusFilter !== "all") {
      result = result.filter(
        (a: any) =>
          (a.clinicalStatus?.coding?.[0]?.code || "").toLowerCase() ===
          allergyStatusFilter.toLowerCase(),
      );
    }

    if (allergyCriticalityFilter !== "all") {
      result = result.filter(
        (a: any) =>
          (a.criticality || "").toLowerCase() ===
          allergyCriticalityFilter.toLowerCase(),
      );
    }

    if (!localSearch) return result;
    const query = localSearch.toLowerCase().trim();
    return result.filter((a: any) => {
      const name = (
        a.code?.text ||
        a.code?.coding?.[0]?.display ||
        ""
      ).toLowerCase();
      const status = (a.clinicalStatus?.coding?.[0]?.code || "").toLowerCase();
      const criticality = (a.criticality || "").toLowerCase();
      const category = (a.category?.[0] || "").toLowerCase();
      return (
        name.includes(query) ||
        status.includes(query) ||
        criticality.includes(query) ||
        category.includes(query)
      );
    });
  }, [alergy, localSearch, allergyStatusFilter, allergyCriticalityFilter]);

  const filteredProcedures = useMemo(() => {
    let result = [...procedure].sort(
      (a: any, b: any) =>
        new Date(b.performedDateTime || 0).getTime() -
        new Date(a.performedDateTime || 0).getTime(),
    );

    if (procedureStatusFilter !== "all") {
      result = result.filter(
        (p: any) =>
          (p.status || "").toLowerCase() ===
          procedureStatusFilter.toLowerCase(),
      );
    }

    if (!localSearch) return result;
    const query = localSearch.toLowerCase().trim();
    return result.filter((p: any) => {
      const name = (
        p.code?.text ||
        p.code?.coding?.[0]?.display ||
        ""
      ).toLowerCase();
      const status = (p.status || "").toLowerCase();
      const date = p.performedDateTime
        ? new Date(p.performedDateTime).toLocaleDateString().toLowerCase()
        : "";
      return (
        name.includes(query) || status.includes(query) || date.includes(query)
      );
    });
  }, [procedure, localSearch, procedureStatusFilter]);

  const filteredCarePlans = useMemo(() => {
    const sorted = [...carePlan].sort(
      (a: any, b: any) =>
        new Date(b.created || b.period?.start || 0).getTime() -
        new Date(a.created || a.period?.start || 0).getTime(),
    );
    if (!localSearch) return sorted;
    const query = localSearch.toLowerCase().trim();
    return sorted.filter((cp: any) => {
      const name = (cp.category?.[0]?.coding?.[0]?.display || "").toLowerCase();
      const status = (cp.status || "").toLowerCase();
      const intent = (cp.intent || "").toLowerCase();
      return (
        name.includes(query) || status.includes(query) || intent.includes(query)
      );
    });
  }, [carePlan, localSearch]);

  const filteredImmunizations = useMemo(() => {
    let result = [...immunization].sort(
      (a: any, b: any) =>
        new Date(b.occurrenceDateTime || b.recorded || 0).getTime() -
        new Date(a.occurrenceDateTime || a.recorded || 0).getTime(),
    );

    if (immunizationStatusFilter !== "all") {
      result = result.filter(
        (i: any) =>
          (i.status || "").toLowerCase() ===
          immunizationStatusFilter.toLowerCase(),
      );
    }

    if (!localSearch) return result;
    const query = localSearch.toLowerCase().trim();
    return result.filter((i: any) => {
      const name = (
        i.vaccineCode?.text ||
        i.vaccineCode?.coding?.[0]?.display ||
        ""
      ).toLowerCase();
      const status = (i.status || "").toLowerCase();
      const date = i.occurrenceDateTime
        ? new Date(i.occurrenceDateTime).toLocaleDateString().toLowerCase()
        : "";
      return (
        name.includes(query) || status.includes(query) || date.includes(query)
      );
    });
  }, [immunization, localSearch, immunizationStatusFilter]);

  const filteredReports = useMemo(() => {
    const sorted = [...DiagnosticReport].sort(
      (a: any, b: any) =>
        new Date(b.effectiveDateTime || b.issued || 0).getTime() -
        new Date(a.effectiveDateTime || a.issued || 0).getTime(),
    );
    if (!localSearch) return sorted;
    const query = localSearch.toLowerCase().trim();
    return sorted.filter((dr: any) => {
      const name = (
        dr.code?.text ||
        dr.code?.coding?.[0]?.display ||
        ""
      ).toLowerCase();
      const status = (dr.status || "").toLowerCase();
      const category = (
        dr.category?.[0]?.coding?.[0]?.display || ""
      ).toLowerCase();
      return (
        name.includes(query) ||
        status.includes(query) ||
        category.includes(query)
      );
    });
  }, [DiagnosticReport, localSearch]);

  const filteredDocuments = useMemo(() => {
    let result = [...documents].sort((a: any, b: any) =>
      new Date(b.Date || 0).getTime() - new Date(a.Date || 0).getTime()
    );

    if (documentStatusFilter !== "all") {
      result = result.filter(
        (doc: any) =>
          (doc.Docstatus || "").toLowerCase() ===
          documentStatusFilter.toLowerCase(),
      );
    }

    if (documentTypeFilter !== "all") {
      result = result.filter(
        (doc: any) =>
          (doc.Type || "").toLowerCase() === documentTypeFilter.toLowerCase(),
      );
    }

    if (!localSearch) return result;
    const query = localSearch.toLowerCase().trim();
    return result.filter((doc: any) => {
      const name = (doc.Name || "").toLowerCase();
      const status = (doc.Docstatus || "").toLowerCase();
      const type = (doc.Type || "").toLowerCase();
      const contentStr = Array.isArray(doc.Content)
        ? doc.Content.join(" ").toLowerCase()
        : "";
      return (
        name.includes(query) ||
        status.includes(query) ||
        type.includes(query) ||
        contentStr.includes(query)
      );
    });
  }, [documents, localSearch, documentStatusFilter, documentTypeFilter]);

  const filterDocumentsByKeyword = useCallback(
    (keyword: string) => {
      const query = keyword.toLowerCase().trim();
      return documents
        .filter((doc: any) => {
          const name = (doc.Name || "").toLowerCase();
          const type = (doc.Type || "").toLowerCase();
          const contentStr = Array.isArray(doc.Content)
            ? doc.Content.join(" ").toLowerCase()
            : "";
          return (
            fuzzyMatch(name, query) ||
            fuzzyMatch(type, query) ||
            contentStr.includes(query)
          );
        })
        .sort(
          (a: any, b: any) =>
            new Date(b.Date || 0).getTime() - new Date(a.Date || 0).getTime(),
        );
    },
    [documents],
  );

  const filteredAdvancedDirectives = useMemo(
    () => filterDocumentsByKeyword(ADVANCED_DIRECTIVES_KEYWORD),
    [filterDocumentsByKeyword],
  );

  const filteredEvaluations = useMemo(
    () => filterDocumentsByKeyword(EVALUATIONS_KEYWORD),
    [filterDocumentsByKeyword],
  );

  const hospiceDetection = useMemo(() => {
    const keyword = "hospice";
    const sources: { label: string; tab: string; index: number; anchorId?: string }[] = [];

    Condition.forEach((c: any, i: number) => {
      const text = (c.code?.text || c.code?.coding?.[0]?.display || "").toLowerCase();
      const codes = (c.code?.coding || []).map((cd: any) => (cd.code || "").toLowerCase()).join(" ");
      if (text.includes(keyword) || codes.includes(keyword)) {
        sources.push({ label: `Condition: ${c.code?.text || c.code?.coding?.[0]?.display || "Unknown"}`, tab: "conditions", index: i });
      }
    });

    carePlan.forEach((cp: any, i: number) => {
      const title = (cp.title || "").toLowerCase();
      const desc = (cp.description || "").toLowerCase();
      const cat = (cp.category?.[0]?.coding?.[0]?.display || "").toLowerCase();
      if (title.includes(keyword) || desc.includes(keyword) || cat.includes(keyword)) {
        sources.push({ label: `Care Plan: ${cp.title || cp.category?.[0]?.coding?.[0]?.display || "Unknown"}`, tab: "careplans", index: i });
      }
    });

    documents.forEach((d: any, i: number) => {
      const name = (d.Name || "").toLowerCase();
      const type = (d.Type || "").toLowerCase();
      const content = Array.isArray(d.Content) ? d.Content.join(" ").toLowerCase() : "";
      if (name.includes(keyword) || type.includes(keyword) || content.includes(keyword)) {
        sources.push({ label: `Document: ${d.Name || "Unknown"}`, tab: "documents", index: i, anchorId: `doc-${i}` });
      }
    });

    Observation.forEach((o: any, i: number) => {
      const name = (o.code?.text || o.code?.coding?.[0]?.display || "").toLowerCase();
      const val = (o.valueString || o.valueCodeableConcept?.text || "").toLowerCase();
      if (name.includes(keyword) || val.includes(keyword)) {
        sources.push({ label: `Observation: ${o.code?.text || o.code?.coding?.[0]?.display || "Unknown"}`, tab: "observations", index: i });
      }
    });

    return { found: sources.length > 0, sources };
  }, [Condition, carePlan, documents, Observation]);

  const surgicalProcedures = useMemo(() => {
    return procedure
      .filter((p: any) => {
        const codes = (p.code?.coding || [])
          .map((c: any) => c.code)
          .filter(Boolean);
        return codes.some((code: string) => {
          const num = parseInt(code, 10);
          return (
            !isNaN(num) && num >= SURGICAL_CPT_MIN && num <= SURGICAL_CPT_MAX
          );
        });
      })
      .sort(
        (a: any, b: any) =>
          new Date(b.performedDateTime || 0).getTime() -
          new Date(a.performedDateTime || 0).getTime(),
      );
  }, [procedure]);

  const fallHistoryConditions = useMemo(() => {
    return Condition.filter((c: any) => {
      const codes = (c.code?.coding || []).map((cd: any) =>
        (cd.code || "").toLowerCase(),
      );
      const text = (
        c.code?.text ||
        c.code?.coding?.[0]?.display ||
        ""
      ).toLowerCase();
      return (
        codes.some((code: string) => FALL_HISTORY_ICD_CODES.includes(code)) ||
        FALL_KEYWORDS.some((k) => text.includes(k))
      );
    }).sort(
      (a: any, b: any) =>
        new Date(b.recordedDate || b.onsetDateTime || 0).getTime() -
        new Date(a.recordedDate || a.onsetDateTime || 0).getTime(),
    );
  }, [Condition]);

  const codeStatusDetection = useMemo(() => {
    const sources: {
      label: string;
      tab: string;
      index: number;
      anchorId?: string;
      matchedTerm: string;
    }[] = [];
    const scan = (text: string) =>
      CODE_STATUS_KEYWORDS.find((k) => text.toLowerCase().includes(k));

    documents.forEach((d: any, i: number) => {
      const content = Array.isArray(d.Content) ? d.Content.join(" ") : "";
      const hit = scan(`${d.Name || ""} ${content}`);
      if (hit)
        sources.push({
          label: `Document: ${d.Name || "Unknown"}`,
          tab: "documents",
          index: i,
          anchorId: `doc-${i}`,
          matchedTerm: hit,
        });
    });
    carePlan.forEach((cp: any, i: number) => {
      const hit = scan(`${cp.title || ""} ${cp.description || ""}`);
      if (hit)
        sources.push({
          label: `Care Plan: ${cp.title || "Unknown"}`,
          tab: "careplans",
          index: i,
          matchedTerm: hit,
        });
    });

    return { found: sources.length > 0, sources };
  }, [documents, carePlan]);

  const matchesObservationSearch = useCallback(
    (o: any, query: string) => {
      const name = (
        o.code?.text ||
        o.code?.coding?.[0]?.display ||
        ""
      ).toLowerCase();
      const category = (
        o.category?.[0]?.coding?.[0]?.display || ""
      ).toLowerCase();
      const val = o.valueQuantity?.value
        ? String(o.valueQuantity.value).toLowerCase()
        : "";
      const unit = (o.valueQuantity?.unit || "").toLowerCase();
      return (
        name.includes(query) ||
        category.includes(query) ||
        val.includes(query) ||
        unit.includes(query)
      );
    },
    [],
  );

  const vitalSignObservations = useMemo(() => {
    const sorted = Observation.filter((o: any) =>
      (o.category || []).some((cat: any) =>
        (cat.coding || []).some((c: any) => c.code === "vital-signs"),
      ),
    ).sort(
      (a: any, b: any) =>
        new Date(a.effectiveDateTime || a.issued || 0).getTime() -
        new Date(b.effectiveDateTime || b.issued || 0).getTime(),
    );
    if (!localSearch) return sorted;
    const query = localSearch.toLowerCase().trim();
    return sorted.filter((o: any) => matchesObservationSearch(o, query));
  }, [Observation, localSearch, matchesObservationSearch]);

  const groupedVitalSigns = useMemo(() => {
    const groups: Record<string, any[]> = {};
    vitalSignObservations.forEach((o: any) => {
      const name = (
        o.code?.text ||
        o.code?.coding?.[0]?.display ||
        ""
      ).toLowerCase();
      const groupKey =
        Object.keys(VITAL_SIGN_GROUPS).find((g) =>
          VITAL_SIGN_GROUPS[g].some((kw) => name.includes(kw)),
        ) || "Other Vitals";
      if (!groups[groupKey]) groups[groupKey] = [];
      groups[groupKey].push(o);
    });
    if (vitalsLatestOnly) {
      Object.keys(groups).forEach((k) => {
        groups[k] = [groups[k][groups[k].length - 1]];
      });
    }
    return groups;
  }, [vitalSignObservations, vitalsLatestOnly]);

  const labObservations = useMemo(() => {
    const sorted = Observation.filter((o: any) =>
      (o.category || []).some((cat: any) =>
        (cat.coding || []).some((c: any) => c.code === "laboratory"),
      ),
    ).sort(
      (a: any, b: any) =>
        new Date(b.effectiveDateTime || b.issued || 0).getTime() -
        new Date(a.effectiveDateTime || a.issued || 0).getTime(),
    );
    if (!localSearch) return sorted;
    const query = localSearch.toLowerCase().trim();
    return sorted.filter((o: any) => matchesObservationSearch(o, query));
  }, [Observation, localSearch, matchesObservationSearch]);

  const extractWoundFields = useCallback(
    (content: string[]): Record<string, string> => {
      const text = Array.isArray(content) ? content.join("\n") : "";
      const found: Record<string, string> = {};
      WOUND_FIELD_LABELS.forEach(({ key, regex }) => {
        const match = text.match(regex);
        if (match?.[1]) found[key] = match[1].trim();
      });
      return found;
    },
    [],
  );

  const handleOpenProcedure = useCallback((p: any) => {
    setSelectedProcedure(p);
    setIsProcedureOpen(true);
  }, []);

  const generateHtmlOutput = useCallback(() => {
    let html = `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <title>Patient Record - ${fullName}</title>
        <style>
          body {
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
            background-color: #ffffff;
            color: #000000;
            margin: 0;
            padding: 40px;
            line-height: 1.5;
          }
          h1 {
            font-size: 24px;
            font-weight: bold;
            margin-bottom: 24px;
            border-bottom: 2px solid #eaeaea;
            padding-bottom: 8px;
          }
          h2 {
            font-size: 20px;
            font-weight: bold;
            margin-top: 32px;
            margin-bottom: 16px;
            padding-bottom: 4px;
            border-bottom: 1px solid #eaeaea;
          }
          h3 {
            font-size: 16px;
            font-weight: bold;
            margin: 0 0 8px 0;
          }
          p {
            margin: 0 0 8px 0;
            font-size: 14px;
          }
          .grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
            gap: 16px;
          }
          .card {
            border: 1px solid #e0e0e0;
            border-radius: 8px;
            padding: 16px;
            background-color: #fafafa;
          }
          .label {
            font-weight: bold;
            color: #333;
          }
          .badge {
            display: inline-block;
            padding: 2px 8px;
            font-size: 12px;
            font-weight: bold;
            background-color: #f0f0f0;
            border-radius: 12px;
            margin-left: 8px;
          }
          .section-list {
            margin: 4px 0 8px 16px;
            padding: 0;
            font-size: 14px;
          }
          .search-bar-wrapper {
            position: sticky;
            top: 0;
            z-index: 100;
            background: #fff;
            padding: 12px 0 12px 0;
            border-bottom: 1px solid #eaeaea;
            margin-bottom: 24px;
          }
          .search-bar-wrapper input {
            width: 100%;
            max-width: 480px;
            padding: 8px 14px;
            font-size: 14px;
            border: 1px solid #ccc;
            border-radius: 6px;
            outline: none;
            box-sizing: border-box;
          }
          .search-bar-wrapper input:focus {
            border-color: #1976d2;
            box-shadow: 0 0 0 2px rgba(25,118,210,0.15);
          }
          .card[hidden] { display: none !important; }
          .hospice-banner {
            display: flex;
            align-items: flex-start;
            gap: 12px;
            background: rgba(124, 58, 237, 0.07);
            border: 1px solid rgba(124, 58, 237, 0.3);
            border-radius: 8px;
            padding: 14px 18px;
            margin-bottom: 28px;
            width :50%
          }
          .hospice-banner-icon {
            font-size: 20px;
            flex-shrink: 0;
            margin-top: 1px;
          }
          .hospice-banner-title {
            font-size: 15px;
            font-weight: bold;
            color: #7c3aed;
            margin: 0 0 6px 0;
          }
          .hospice-banner-source {
            font-size: 13px;
            color: #4b5563;
            margin: 3px 0;
          }
          .hospice-banner-link {
            color: #7c3aed;
            text-decoration: underline;
            cursor: pointer;
          }
          .hospice-banner-link:hover {
            color: #5b21b6;
          }
          .card:target {
            outline: 2px solid #7c3aed;
            outline-offset: 2px;
          }
        </style>
      </head>
      <body>
        <div class="search-bar-wrapper">
          <input id="pageSearch" type="search" placeholder="Search within this page..." aria-label="Search patient record" />
        </div>
        <h1>Patient Record</h1>
        ${hospiceDetection.found ? `
        <div class="hospice-banner">
          <span class="hospice-banner-icon">🟣</span>
          <div>
            <p class="hospice-banner-title">Hospice Detected</p>
            <p style="font-size:13px;color:#6b7280;margin:0 0 8px 0;">The keyword "hospice" was found in the following records:</p>
            ${hospiceDetection.sources.map(s => s.anchorId
              ? `<p class="hospice-banner-source">• <a href="#${s.anchorId}" class="hospice-banner-link">${s.label}</a></p>`
              : `<p class="hospice-banner-source">• ${s.label}</p>`
            ).join("")}
          </div>
        </div>` : ""}
    `;

    // 1. Patient Details
    html += `
      <h2>Patient Details</h2>
      <div class="card" style="background-color: #fff; max-width: 600px;">
        <p><span class="label">Name:</span> ${fullName}</p>
        <p><span class="label">ID:</span> ${Patient?.id || "N/A"}</p>
        <p><span class="label">Gender:</span> ${Patient?.gender || "N/A"}</p>
        <p><span class="label">DOB:</span> ${Patient?.birthDate || "N/A"}</p>
        <p><span class="label">Address:</span> ${fullAddress}</p>
      </div>
    `;

    // Helper for cards
    const sanitizeAttr = (s: string) => s.replace(/['"<>&]/g, " ");
    const renderCard = (title: string, details: { label: string; value: string }[], badges: string[] = [], customHtml = "") => {
      const searchText = sanitizeAttr(
        [title, ...badges, ...details.map(d => d.value)].join(" ").toLowerCase()
      );
      let cardHtml = `<div class="card" data-search="${searchText}">`;
      cardHtml += `<h3>${title}`;
      badges.forEach(b => {
        if (b) cardHtml += `<span class="badge">${b}</span>`;
      });
      cardHtml += `</h3>`;
      details.forEach(d => {
        if (d.value && d.value !== "N/A" && d.value !== "Unknown" && d.value.trim() !== "") {
          cardHtml += `<p><span class="label">${d.label}:</span> ${d.value}</p>`;
        }
      });
      if (customHtml) {
        cardHtml += customHtml;
      }
      cardHtml += `</div>`;
      return cardHtml;
    };

    // Helper to format conditions/locations array
    const formatList = (arr: any[], mapper: (item: any) => string) => {
      if (!arr || arr.length === 0) return "";
      return `<ul class="section-list">` + arr.map(item => `<li>${mapper(item)}</li>`).join("") + `</ul>`;
    };

    // 2. Encounters
    const sortedEncounters = [...Encounters].sort(
      (a: any, b: any) =>
        new Date(b.start || b.period?.start || 0).getTime() -
        new Date(a.start || a.period?.start || 0).getTime(),
    );
    html += `<h2>Encounters (${sortedEncounters.length})</h2>`;
    if (sortedEncounters.length === 0) {
      html += `<p>No encounters found.</p>`;
    } else {
      html += `<div class="grid">`;
      sortedEncounters.forEach((e: any) => {
        const type = e.type?.[0]?.coding?.[0]?.display || "Encounter";
        const status = e.status || "Unknown";
        const periodStart = e.start ? new Date(e.start).toLocaleString() : e.period?.start ? new Date(e.period.start).toLocaleString() : "";
        const periodEnd = e.end ? new Date(e.end).toLocaleString() : e.period?.end ? new Date(e.period.end).toLocaleString() : "";
        
        let customHtml = "";
        if (e.conditions && e.conditions.length > 0) {
          customHtml += `<p><span class="label">Conditions:</span></p>` + formatList(e.conditions, c => c.code?.coding?.[0]?.display || c.code?.text || "Unknown Condition");
        }
        if (e.locations && e.locations.length > 0) {
          customHtml += `<p><span class="label">Locations:</span></p>` + formatList(e.locations, l => l.location?.display || "Unknown Location");
        }
        if (e.participants && e.participants.length > 0) {
          customHtml += `<p><span class="label">Participants:</span></p>` + formatList(e.participants, p => p.individual?.display || "Unknown Participant");
        }

        html += renderCard(type, [
          { label: "Class", value: e.class?.display || e.class?.code || "" },
          { label: "Start Time", value: periodStart },
          { label: "End Time", value: periodEnd }
        ], [status], customHtml);
      });
      html += `</div>`;
    }

    // 3. Conditions
    const sortedConditions = [...Condition].sort(
      (a: any, b: any) =>
        new Date(b.recordedDate || b.onsetDateTime || 0).getTime() -
        new Date(a.recordedDate || a.onsetDateTime || 0).getTime(),
    );
    html += `<h2>Conditions (${sortedConditions.length})</h2>`;
    if (sortedConditions.length === 0) {
      html += `<p>No conditions found.</p>`;
    } else {
      html += `<div class="grid">`;
      sortedConditions.forEach((c: any) => {
        const name = c.code?.text || c.code?.coding?.[0]?.display || "Condition";
        const clinStatus = c.clinicalStatus?.coding?.[0]?.code || "";
        const verStatus = c.verificationStatus?.coding?.[0]?.code || "";
        const recordedOn = c.recordedDate ? new Date(c.recordedDate).toLocaleDateString() : "N/A";
        const icdCodes = (c.code?.coding || [])
          .filter((cd: any) => cd.system?.toLowerCase().includes("icd") && cd.code)
          .map((cd: any) => {
            const version = cd.system?.toLowerCase().includes("icd-9") ? "ICD-9" : "ICD-10";
            return `${version}: ${cd.code}`;
          })
          .join(", ");
        const category = c.category?.[0]?.coding?.[0]?.display || "";

        html += renderCard(name, [
          { label: "Clinical Status", value: clinStatus },
          { label: "Verification Status", value: verStatus },
          { label: "Category", value: category },
          { label: "Recorded on", value: recordedOn },
          { label: "ICD Codes", value: icdCodes }
        ]);
      });
      html += `</div>`;
    }

    // 4. Observations
    const sortedObservations = [...Observation].sort(
      (a: any, b: any) =>
        new Date(b.effectiveDateTime || b.issued || 0).getTime() -
        new Date(a.effectiveDateTime || a.issued || 0).getTime(),
    );
    html += `<h2>Observations (${sortedObservations.length})</h2>`;
    if (sortedObservations.length === 0) {
      html += `<p>No observations found.</p>`;
    } else {
      html += `<div class="grid">`;
      sortedObservations.forEach((o: any) => {
        const name = o.code?.text || o.code?.coding?.[0]?.display || "Observation";
        const category = o.category?.[0]?.coding?.[0]?.display || "";
        const valStr = o.valueQuantity ? `${o.valueQuantity.value} ${o.valueQuantity.unit || ""}` : (o.valueString || "N/A");
        const date = o.effectiveDateTime ? new Date(o.effectiveDateTime).toLocaleDateString() : "N/A";
        const refRange = o.referenceRange?.[0] ? `${o.referenceRange[0].low?.value || ""} - ${o.referenceRange[0].high?.value || ""} ${o.referenceRange[0].high?.unit || ""}` : "";
        
        let customHtml = "";
        if (o.component && o.component.length > 0) {
          customHtml += `<p><span class="label">Components:</span></p>` + formatList(o.component, c => `${c.code?.text || c.code?.coding?.[0]?.display || "Unknown"}: ${c.valueQuantity ? `${c.valueQuantity.value} ${c.valueQuantity.unit || ""}` : (c.valueString || "N/A")}`);
        }

        html += renderCard(name, [
          { label: "Category", value: category },
          { label: "Value", value: valStr },
          { label: "Reference Range", value: refRange },
          { label: "Effective Date", value: date }
        ], [], customHtml);
      });
      html += `</div>`;
    }

    // 5. Medications — active only
    const activeMeds = MedicationRequest.filter((m: any) => {
      const s = (m.status || "").toLowerCase();
      return s !== "completed" && s !== "stopped";
    }).sort(
      (a: any, b: any) =>
        new Date(b.authoredOn || 0).getTime() -
        new Date(a.authoredOn || 0).getTime(),
    );
    html += `<h2>Medications (${activeMeds.length} active of ${MedicationRequest.length})</h2>`;
    if (activeMeds.length === 0) {
      html += `<p>No active medications found.</p>`;
    } else {
      html += `<div class="grid">`;
      activeMeds.forEach((m: any) => {
        const name = getMedicationName(m);
        const status = m.status || "";
        const intent = m.intent || "";
        const dosage = m.dosageInstruction?.[0]?.text || "N/A";
        const authoredOn = m.authoredOn ? new Date(m.authoredOn).toLocaleDateString() : "";
        const requester = m.requester?.display || "";
        const searchText = [name, status, intent, dosage, requester].join(" ").toLowerCase().replace(/['"<>&]/g, " ");

        html += `<div class="card" data-search="${searchText}">`;
        html += `<h3>${name}<span class="badge">${status}</span></h3>`;
        if (intent) html += `<p><span class="label">Intent:</span> ${intent}</p>`;
        if (dosage && dosage !== "N/A") html += `<p><span class="label">Dosage:</span> ${dosage}</p>`;
        if (authoredOn) html += `<p><span class="label">Authored On:</span> ${authoredOn}</p>`;
        if (requester) html += `<p><span class="label">Requester:</span> ${requester}</p>`;
        html += `</div>`;
      });
      html += `</div>`;
    }

    // 6. Allergies
    const sortedAllergies = [...alergy].sort(
      (a: any, b: any) =>
        new Date(b.recordedDate || b.onsetDateTime || 0).getTime() -
        new Date(a.recordedDate || a.onsetDateTime || 0).getTime(),
    );
    html += `<h2>Allergies (${sortedAllergies.length})</h2>`;
    if (sortedAllergies.length === 0) {
      html += `<p>No allergies found.</p>`;
    } else {
      html += `<div class="grid">`;
      sortedAllergies.forEach((a: any) => {
        const name = a.code?.text || a.code?.coding?.[0]?.display || "Allergy";
        const status = a.clinicalStatus?.coding?.[0]?.code || "";
        const criticality = a.criticality || "";
        const category = a.category?.[0] || "";
        const recordedOn = a.recordedDate ? new Date(a.recordedDate).toLocaleDateString() : "";
        
        let customHtml = "";
        if (a.reaction && a.reaction.length > 0) {
          customHtml += `<p><span class="label">Reactions:</span></p>` + formatList(a.reaction, r => r.manifestation?.[0]?.coding?.[0]?.display || r.manifestation?.[0]?.text || "Unknown Reaction");
        }

        html += renderCard(name, [
          { label: "Clinical Status", value: status },
          { label: "Criticality", value: criticality },
          { label: "Category", value: category },
          { label: "Recorded On", value: recordedOn }
        ], [], customHtml);
      });
      html += `</div>`;
    }

    // 7. Procedures
    const sortedProcedures = [...procedure].sort(
      (a: any, b: any) =>
        new Date(b.performedDateTime || 0).getTime() -
        new Date(a.performedDateTime || 0).getTime(),
    );
    html += `<h2>Procedures (${sortedProcedures.length})</h2>`;
    if (sortedProcedures.length === 0) {
      html += `<p>No procedures found.</p>`;
    } else {
      html += `<div class="grid">`;
      sortedProcedures.forEach((p: any) => {
        const name = p.code?.text || p.code?.coding?.[0]?.display || p.code?.extension?.[0]?.valueCodeableConcept?.coding?.[0]?.display || "Procedure";
        const status = p.status || "";
        const performed = p.performedDateTime ? new Date(p.performedDateTime).toLocaleDateString() : "N/A";
        const category = p.category?.coding?.[0]?.display || "";
        const bodySite = p.bodySite?.[0]?.coding?.[0]?.display || "";

        html += renderCard(name, [
          { label: "Category", value: category },
          { label: "Body Site", value: bodySite },
          { label: "Performed on", value: performed }
        ], [status]);
      });
      html += `</div>`;
    }

    // 8. Care Plans
    const sortedCarePlans = [...carePlan].sort(
      (a: any, b: any) =>
        new Date(b.created || b.period?.start || 0).getTime() -
        new Date(a.created || a.period?.start || 0).getTime(),
    );
    html += `<h2>Care Plans (${sortedCarePlans.length})</h2>`;
    if (sortedCarePlans.length === 0) {
      html += `<p>No care plans found.</p>`;
    } else {
      html += `<div class="grid">`;
      sortedCarePlans.forEach((cp: any) => {
        const name = cp.title || cp.category?.[0]?.coding?.[0]?.display || "Care Plan";
        const status = cp.status || "";
        const intent = cp.intent || "";
        const description = cp.description || "";
        const created = cp.created ? new Date(cp.created).toLocaleDateString() : "";
        
        let customHtml = "";
        if (cp.addresses && cp.addresses.length > 0) {
          customHtml += `<p><span class="label">Addresses:</span></p>` + formatList(cp.addresses, a => a.display || a.reference || "Unknown");
        }
        if (cp.activity && cp.activity.length > 0) {
          customHtml += `<p><span class="label">Activities:</span></p>` + formatList(cp.activity, a => a.detail?.code?.coding?.[0]?.display || a.detail?.description || "Unknown Activity");
        }

        html += renderCard(name, [
          { label: "Intent", value: intent },
          { label: "Description", value: description },
          { label: "Created On", value: created }
        ], [status], customHtml);
      });
      html += `</div>`;
    }

    // 9. Immunizations
    const sortedImmunizations = [...immunization].sort(
      (a: any, b: any) =>
        new Date(b.occurrenceDateTime || b.recorded || 0).getTime() -
        new Date(a.occurrenceDateTime || a.recorded || 0).getTime(),
    );
    html += `<h2>Immunizations (${sortedImmunizations.length})</h2>`;
    if (sortedImmunizations.length === 0) {
      html += `<p>No immunizations found.</p>`;
    } else {
      html += `<div class="grid">`;
      sortedImmunizations.forEach((im: any) => {
        const name = im.vaccineCode?.text || im.vaccineCode?.coding?.[0]?.display || "Immunization";
        const status = im.status || "";
        const date = im.occurrenceDateTime ? new Date(im.occurrenceDateTime).toLocaleDateString() : "N/A";
        const lotNumber = im.lotNumber || "";
        const expirationDate = im.expirationDate ? new Date(im.expirationDate).toLocaleDateString() : "";
        const site = im.site?.coding?.[0]?.display || "";
        const route = im.route?.coding?.[0]?.display || "";

        html += renderCard(name, [
          { label: "Date", value: date },
          { label: "Lot Number", value: lotNumber },
          { label: "Expiration Date", value: expirationDate },
          { label: "Site", value: site },
          { label: "Route", value: route }
        ], [status]);
      });
      html += `</div>`;
    }

    // 10. Diagnostic Reports
    const sortedReports = [...DiagnosticReport].sort(
      (a: any, b: any) =>
        new Date(b.effectiveDateTime || b.issued || 0).getTime() -
        new Date(a.effectiveDateTime || a.issued || 0).getTime(),
    );
    html += `<h2>Diagnostic Reports (${sortedReports.length})</h2>`;
    if (sortedReports.length === 0) {
      html += `<p>No diagnostic reports found.</p>`;
    } else {
      html += `<div class="grid">`;
      sortedReports.forEach((dr: any) => {
        const name = dr.code?.text || dr.code?.coding?.[0]?.display || "Report";
        const status = dr.status || "";
        const category = dr.category?.[0]?.coding?.[0]?.display || "";
        const effectiveDate = dr.effectiveDateTime ? new Date(dr.effectiveDateTime).toLocaleDateString() : "";
        const issuedDate = dr.issued ? new Date(dr.issued).toLocaleString() : "";
        
        let customHtml = "";
        if (dr.result && dr.result.length > 0) {
          customHtml += `<p><span class="label">Results:</span></p>` + formatList(dr.result, r => r.display || r.reference || "Result item");
        }

        html += renderCard(name, [
          { label: "Category", value: category },
          { label: "Effective Date", value: effectiveDate },
          { label: "Issued Date", value: issuedDate }
        ], [status], customHtml);
      });
      html += `</div>`;
    }

    // Helper to extract text content from document Content field
    const extractDocContent = (content: string[] | undefined): string => {
      if (!content || content.length === 0) return "";
      const raw = content[0];
      if (!raw) return "";

      // Try XML parsing
      if (raw.trim().startsWith("<?xml") || raw.trim().startsWith("<")) {
        try {
          const parser = new DOMParser();
          const xmlDoc = parser.parseFromString(raw, "text/xml");
          const title = xmlDoc.getElementsByTagName("title")[0]?.textContent || "";
          const sections = xmlDoc.getElementsByTagName("section");
          if (sections.length > 0) {
            let out = "";
            if (title) out += `<h4 style="margin:8px 0 4px 0;font-weight:bold;">${title}</h4>`;
            Array.from(sections).forEach((sec) => {
              const secTitle = sec.getElementsByTagName("title")[0]?.textContent || "";
              const secTextEl = sec.getElementsByTagName("text")[0];
              const secText = secTextEl ? secTextEl.textContent : "";
              if (secTitle) out += `<p style="margin:6px 0 2px 0;"><span class="label">${secTitle}</span></p>`;
              if (secText) out += `<p style="margin:0 0 8px 0;white-space:pre-wrap;">${secText}</p>`;
            });
            return out;
          }
          // No sections, just dump text content
          const textContent = xmlDoc.documentElement?.textContent || "";
          return textContent ? `<p style="white-space:pre-wrap;">${textContent}</p>` : "";
        } catch (e) {
          // Fall through
        }
      }

      // Try JSON
      try {
        const json = JSON.parse(raw);
        return `<pre style="white-space:pre-wrap;font-size:13px;margin:8px 0;">${JSON.stringify(json, null, 2)}</pre>`;
      } catch (e) {
        // Not JSON
      }

      // Plain text
      return `<p style="white-space:pre-wrap;">${raw}</p>`;
    };

    // 11. Documents
    const documentAnchorIds = new Map<any, number>(
      documents.map((d: any, i: number) => [d, i]),
    );
    const sortedDocuments = [...documents].sort(
      (a: any, b: any) =>
        new Date(b.Date || 0).getTime() - new Date(a.Date || 0).getTime(),
    );
    html += `<h2>Documents (${sortedDocuments.length})</h2>`;
    if (sortedDocuments.length === 0) {
      html += `<p>No documents found.</p>`;
    } else {
      sortedDocuments.forEach((d: any) => {
        const name = d.Name || "Document";
        const type = d.Type || "N/A";
        const docStatus = d.Docstatus || "";
        const date = d.Date ? new Date(d.Date).toLocaleString() : "";
        const author = d.Author || "";
        const contentHtml = extractDocContent(d.Content);
        const anchorId = `doc-${documentAnchorIds.get(d)}`;

        const docSearchText = sanitizeAttr([name, type, docStatus, author].join(" ").toLowerCase());
        html += `<div class="card" id="${anchorId}" style="margin-bottom:16px;" data-search="${docSearchText}">`;
        html += `<h3>${name}`;
        if (docStatus) html += `<span class="badge">${docStatus}</span>`;
        html += `</h3>`;
        if (date) html += `<p><span class="label">Date:</span> ${date}</p>`;
        if (type) html += `<p><span class="label">Type:</span> ${type}</p>`;
        if (author) html += `<p><span class="label">Author:</span> ${author}</p>`;
        if (contentHtml) {
          html += `<hr style="border:none;border-top:1px solid #e0e0e0;margin:12px 0;">`;
          html += contentHtml;
        }
        html += `</div>`;
      });
    }

    // 12. Advanced Directives
    html += `<h2>Advanced Directives (${filteredAdvancedDirectives.length})</h2>`;
    if (filteredAdvancedDirectives.length === 0) {
      html += `<p>No advanced directives found.</p>`;
    } else {
      html += `<div class="grid">`;
      filteredAdvancedDirectives.forEach((d: any) => {
        const name = d.Name || "Document";
        const type = d.Type || "";
        const docStatus = d.Docstatus || "";
        const date = d.Date ? new Date(d.Date).toLocaleString() : "";
        html += renderCard(name, [
          { label: "Type", value: type },
          { label: "Date", value: date },
        ], [docStatus]);
      });
      html += `</div>`;
    }

    // 13. Evaluations
    html += `<h2>Evaluations (${filteredEvaluations.length})</h2>`;
    if (filteredEvaluations.length === 0) {
      html += `<p>No evaluations found.</p>`;
    } else {
      html += `<div class="grid">`;
      filteredEvaluations.forEach((d: any) => {
        const name = d.Name || "Document";
        const type = d.Type || "";
        const docStatus = d.Docstatus || "";
        const date = d.Date ? new Date(d.Date).toLocaleString() : "";
        html += renderCard(name, [
          { label: "Type", value: type },
          { label: "Date", value: date },
        ], [docStatus]);
      });
      html += `</div>`;
    }

    // 14. Smoking / Tobacco Use
    const SMOKING_KEYWORDS = ["smoking", "nicotine", "tobacco", "smoker", "cigarette"];
    const SMOKING_ICD_PREFIXES = ["f17", "z87.891", "z72.0"];
    const isSmokingRelated = (text: string) => {
      const t = text.toLowerCase();
      if (SMOKING_KEYWORDS.some(k => t.includes(k))) return true;
      if (SMOKING_ICD_PREFIXES.some(p => t.includes(p))) return true;
      return false;
    };
    const smokingConditions = Condition.filter((c: any) => {
      const name = c.code?.text || c.code?.coding?.[0]?.display || "";
      const codes = (c.code?.coding || []).map((cd: any) => cd.code || "").join(" ");
      return isSmokingRelated(name + " " + codes);
    });
    const smokingObservations = Observation.filter((o: any) => {
      const name = o.code?.text || o.code?.coding?.[0]?.display || "";
      const valStr = o.valueString || o.valueCodeableConcept?.text || o.valueCodeableConcept?.coding?.[0]?.display || "";
      return isSmokingRelated(name + " " + valStr);
    });
    const hasSmokingData = smokingConditions.length > 0 || smokingObservations.length > 0;
    html += `<h2>Smoking / Tobacco Use</h2>`;
    if (!hasSmokingData) {
      html += `<p>No smoking or tobacco use records found.</p>`;
    } else {
      html += `<div class="grid">`;
      smokingConditions.forEach((c: any) => {
        const name = c.code?.text || c.code?.coding?.[0]?.display || "Condition";
        const clinStatus = c.clinicalStatus?.coding?.[0]?.code || "";
        const recordedOn = c.recordedDate ? new Date(c.recordedDate).toLocaleDateString() : "";
        const icdCodes = (c.code?.coding || [])
          .filter((cd: any) => cd.code)
          .map((cd: any) => cd.code)
          .join(", ");
        html += renderCard(name, [
          { label: "Clinical Status", value: clinStatus },
          { label: "ICD Code", value: icdCodes },
          { label: "Recorded On", value: recordedOn },
        ], ["condition"]);
      });
      smokingObservations.forEach((o: any) => {
        const name = o.code?.text || o.code?.coding?.[0]?.display || "Observation";
        const valStr = o.valueString || o.valueCodeableConcept?.text || o.valueCodeableConcept?.coding?.[0]?.display || (o.valueQuantity ? `${o.valueQuantity.value} ${o.valueQuantity.unit || ""}` : "");
        const date = o.effectiveDateTime ? new Date(o.effectiveDateTime).toLocaleDateString() : "";
        html += renderCard(name, [
          { label: "Value", value: valStr },
          { label: "Date", value: date },
        ], ["observation"]);
      });
      html += `</div>`;
    }

    html += `
      <script>
        (function() {
          var input = document.getElementById('pageSearch');
          if (!input) return;
          input.addEventListener('input', function() {
            var q = input.value.trim().toLowerCase();
            document.querySelectorAll('.card[data-search]').forEach(function(card) {
              if (!q || card.getAttribute('data-search').indexOf(q) !== -1) {
                card.removeAttribute('hidden');
              } else {
                card.setAttribute('hidden', '');
              }
            });
          });
        })();
      </script>
      </body>
      </html>
    `;
    return html;
  }, [
    fullName,
    Patient,
    fullAddress,
    Encounters,
    Condition,
    Observation,
    MedicationRequest,
    getMedicationName,
    alergy,
    procedure,
    carePlan,
    immunization,
    DiagnosticReport,
    documents,
    filteredAdvancedDirectives,
    filteredEvaluations,
    hospiceDetection,
  ]);

  const handleViewText = useCallback(() => {
    const html = generateHtmlOutput();
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
  }, [generateHtmlOutput]);

  const totalCount = useMemo(() => {
    switch (activeSection) {
      case "encounters":
        return Encounters.length;
      case "conditions":
        return Condition.length;
      case "observations":
        return Observation.length;
      case "medications":
        return MedicationRequest.length;
      case "allergies":
        return alergy.length;
      case "procedures":
        return procedure.length;
      case "careplans":
        return carePlan.length;
      case "immunizations":
        return immunization.length;
      case "reports":
        return DiagnosticReport.length;
      case "documents":
        return documents.length;
      case "advanced-directives":
        return filteredAdvancedDirectives.length;
      case "evaluations":
        return filteredEvaluations.length;
      case "surgical-history":
        return surgicalProcedures.length;
      case "fall-history":
        return fallHistoryConditions.length;
      case "code-status":
        return codeStatusDetection.sources.length;
      case "vitals":
        return vitalSignObservations.length;
      case "labs":
        return labObservations.length;
      default:
        return 0;
    }
  }, [
    activeSection,
    Encounters.length,
    Condition.length,
    Observation.length,
    MedicationRequest.length,
    alergy.length,
    procedure.length,
    carePlan.length,
    immunization.length,
    DiagnosticReport.length,
    documents.length,
    filteredAdvancedDirectives.length,
    filteredEvaluations.length,
    surgicalProcedures.length,
    fallHistoryConditions.length,
    codeStatusDetection.sources.length,
    vitalSignObservations.length,
    labObservations.length,
  ]);

  const suggestions = useMemo(() => {
    const query = globalSearch.toLowerCase().trim();
    if (query.length < MIN_SEARCH_LENGTH) return [];
    const results: Array<{
      id: string;
      title: string;
      subtitle: string;
      onClick: () => void;
    }> = [];
    // Exact/substring title matches rank above fuzzy/typo-tolerant matches
    const matchRank = (title: string) =>
      title.toLowerCase().includes(query) ? 0 : 1;

    const excerptAroundMatch = (source: string): string => {
      const trimmed = source.trim();
      if (trimmed.length <= 80) return trimmed;
      const matchIndex = trimmed.toLowerCase().indexOf(query);
      if (matchIndex === -1) return trimmed.substring(0, 80) + "...";
      const start = Math.max(0, matchIndex - 30);
      const end = Math.min(trimmed.length, matchIndex + query.length + 50);
      const prefix = start > 0 ? "..." : "";
      const suffix = end < trimmed.length ? "..." : "";
      return prefix + trimmed.substring(start, end) + suffix;
    };

    const getSnippet = (text: string): string => {
      if (!text) return "";
      const lines = text.split(/\r?\n/);
      const matchingLine = lines.find((line) =>
        line.toLowerCase().includes(query),
      );
      if (matchingLine) {
        return excerptAroundMatch(matchingLine);
      }
      return excerptAroundMatch(text);
    };

    // 1. Details
    if (Patient) {
      const detailsFields = [
        Patient.gender || "",
        Patient.birthDate || "",
        Patient.id || "",
        Patient.telecom?.[0]?.value || "",
        fullAddress || "",
      ];
      if (detailsFields.some((f) => fuzzyMatch(f.toLowerCase(), query))) {
        results.push({
          id: "details",
          title: `Patient Profile: ${Patient.name?.[0]?.given?.join(" ")} ${Patient.name?.[0]?.family}`,
          subtitle: "Patient Details",
          onClick: () => {
            const idx = tabs.findIndex((t) => t.id === "details");
            if (idx !== -1) setActiveTab(idx);
          },
        });
      }
    }

    // 2. Encounters
    Encounters.forEach((enc: any) => {
      const typeDisplay = enc.type?.[0]?.coding?.[0]?.display || "Encounter";
      const status = enc.status || "";
      const condNames = (enc.conditions || [])
        .map((c: any) => c.code?.coding?.[0]?.display || c.code?.text || "")
        .join(" ");
      const obsNames = (enc.observations || [])
        .map((o: any) => o.code?.coding?.[0]?.display || "")
        .join(" ");

      if (
        fuzzyMatch(typeDisplay.toLowerCase(), query) ||
        fuzzyMatch(status.toLowerCase(), query) ||
        fuzzyMatch(condNames.toLowerCase(), query) ||
        fuzzyMatch(obsNames.toLowerCase(), query)
      ) {
        results.push({
          id: `encounters-${enc.id || enc.start}`,
          title: getSnippet(typeDisplay),
          subtitle: `Encounters (Status: ${status})`,
          onClick: () => {
            const idx = tabs.findIndex((t) => t.id === "encounters");
            if (idx !== -1) {
              setActiveTab(idx);
              setLocalSearch(globalSearch);
            }
          },
        });
      }
    });

    // 3. Conditions
    Condition.forEach((c: any) => {
      const name = c.code?.text || c.code?.coding?.[0]?.display || "Condition";
      const category = c.category?.[0]?.coding?.[0]?.display || "";
      const clinicalStatus = c.clinicalStatus?.coding?.[0]?.code || "";
      const verificationStatus = c.verificationStatus?.coding?.[0]?.code || "";
      const icdCodes = (c.code?.coding || [])
        .map((cd: any) => cd.code || "")
        .join(" ");
      if (
        fuzzyMatch(name.toLowerCase(), query) ||
        fuzzyMatch(category.toLowerCase(), query) ||
        fuzzyMatch(clinicalStatus.toLowerCase(), query) ||
        fuzzyMatch(verificationStatus.toLowerCase(), query) ||
        icdCodes.toLowerCase().includes(query)
      ) {
        let snippet = name;
        if (icdCodes.toLowerCase().includes(query)) {
          const matchedIcd = (c.code?.coding || []).find((cd: any) =>
            (cd.code || "").toLowerCase().includes(query),
          )?.code;
          snippet = `${name} (ICD-10: ${matchedIcd})`;
        }
        results.push({
          id: `conditions-${c.id || name}`,
          title: getSnippet(snippet),
          subtitle: `Conditions (${category || "Clinical"})`,
          onClick: () => {
            const idx = tabs.findIndex((t) => t.id === "conditions");
            if (idx !== -1) {
              setActiveTab(idx);
              setLocalSearch(globalSearch);
            }
          },
        });
      }
    });

    // 4. Observations
    Observation.forEach((o: any) => {
      const name =
        o.code?.text || o.code?.coding?.[0]?.display || "Observation";
      const category = o.category?.[0]?.coding?.[0]?.display || "";
      const valStr = o.valueQuantity
        ? `${o.valueQuantity.value} ${o.valueQuantity.unit || ""}`
        : "";
      if (
        fuzzyMatch(name.toLowerCase(), query) ||
        fuzzyMatch(category.toLowerCase(), query) ||
        valStr.toLowerCase().includes(query)
      ) {
        results.push({
          id: `observations-${o.id || name}`,
          title: getSnippet(`${name}: ${valStr}`),
          subtitle: `Observations (${category})`,
          onClick: () => {
            const idx = tabs.findIndex((t) => t.id === "observations");
            if (idx !== -1) {
              setActiveTab(idx);
              setLocalSearch(globalSearch);
            }
          },
        });
      }
    });

    // 5. Medications
    MedicationRequest.forEach((m: any) => {
      const name = getMedicationName(m);
      const status = m.status || "";
      const intent = m.intent || "";
      const dosageText = m.dosageInstruction?.[0]?.text || "";
      if (
        fuzzyMatch(name.toLowerCase(), query) ||
        fuzzyMatch(status.toLowerCase(), query) ||
        fuzzyMatch(intent.toLowerCase(), query) ||
        fuzzyMatch(dosageText.toLowerCase(), query)
      ) {
        results.push({
          id: `medications-${m.id || name}`,
          title: getSnippet(`${name} - ${dosageText || status}`),
          subtitle: "Medications",
          onClick: () => {
            const idx = tabs.findIndex((t) => t.id === "medications");
            if (idx !== -1) {
              setActiveTab(idx);
              setLocalSearch(globalSearch);
            }
          },
        });
      }
    });

    // 6. Allergies
    alergy.forEach((a: any) => {
      const name = a.code?.text || a.code?.coding?.[0]?.display || "Allergy";
      const category = a.category?.[0] || "";
      const status = a.clinicalStatus?.coding?.[0]?.code || "";
      const criticality = a.criticality || "";
      if (
        fuzzyMatch(name.toLowerCase(), query) ||
        fuzzyMatch(category.toLowerCase(), query) ||
        fuzzyMatch(status.toLowerCase(), query) ||
        fuzzyMatch(criticality.toLowerCase(), query)
      ) {
        results.push({
          id: `allergies-${a.id || name}`,
          title: getSnippet(name),
          subtitle: `Allergies (${category || criticality})`,
          onClick: () => {
            const idx = tabs.findIndex((t) => t.id === "allergies");
            if (idx !== -1) {
              setActiveTab(idx);
              setLocalSearch(globalSearch);
            }
          },
        });
      }
    });

    // 7. Procedures
    procedure.forEach((p: any) => {
      const name = p.code?.text || p.code?.coding?.[0]?.display || p.code?.extension?.[0]?.valueCodeableConcept?.coding?.[0]?.display || "Procedure";
      const status = p.status || "";
      if (
        fuzzyMatch(name.toLowerCase(), query) ||
        fuzzyMatch(status.toLowerCase(), query)
      ) {
        results.push({
          id: `procedures-${p.id || name}`,
          title: getSnippet(name),
          subtitle: `Procedures (Status: ${status})`,
          onClick: () => {
            const idx = tabs.findIndex((t) => t.id === "procedures");
            if (idx !== -1) {
              setActiveTab(idx);
              setLocalSearch(globalSearch);
            }
          },
        });
      }
    });

    // 8. Care Plans
    carePlan.forEach((cp: any) => {
      const category = cp.category?.[0]?.coding?.[0]?.display || "Care Plan";
      const status = cp.status || "";
      const intent = cp.intent || "";
      const divText = cp.text?.div || "";
      if (
        fuzzyMatch(category.toLowerCase(), query) ||
        fuzzyMatch(status.toLowerCase(), query) ||
        fuzzyMatch(intent.toLowerCase(), query) ||
        divText.toLowerCase().includes(query)
      ) {
        results.push({
          id: `careplans-${cp.id || category}`,
          title: getSnippet(category),
          subtitle: "Care Plans",
          onClick: () => {
            const idx = tabs.findIndex((t) => t.id === "careplans");
            if (idx !== -1) {
              setActiveTab(idx);
              setLocalSearch(globalSearch);
            }
          },
        });
      }
    });

    // 9. Immunizations
    immunization.forEach((i: any) => {
      const name =
        i.vaccineCode?.text ||
        i.vaccineCode?.coding?.[0]?.display ||
        "Vaccination";
      const status = i.status || "";
      if (
        fuzzyMatch(name.toLowerCase(), query) ||
        fuzzyMatch(status.toLowerCase(), query)
      ) {
        results.push({
          id: `immunizations-${i.id || name}`,
          title: getSnippet(name),
          subtitle: "Immunizations",
          onClick: () => {
            const idx = tabs.findIndex((t) => t.id === "immunizations");
            if (idx !== -1) {
              setActiveTab(idx);
              setLocalSearch(globalSearch);
            }
          },
        });
      }
    });

    // 10. Reports
    DiagnosticReport.forEach((dr: any) => {
      const name =
        dr.code?.text || dr.code?.coding?.[0]?.display || "Diagnostic Report";
      const category = dr.category?.[0]?.coding?.[0]?.display || "";
      const status = dr.status || "";
      if (
        fuzzyMatch(name.toLowerCase(), query) ||
        fuzzyMatch(category.toLowerCase(), query) ||
        fuzzyMatch(status.toLowerCase(), query)
      ) {
        results.push({
          id: `reports-${dr.id || name}`,
          title: getSnippet(name),
          subtitle: "Diagnostic Reports",
          onClick: () => {
            const idx = tabs.findIndex((t) => t.id === "reports");
            if (idx !== -1) {
              setActiveTab(idx);
              setLocalSearch(globalSearch);
            }
          },
        });
      }
    });

    // 11. Documents
    const matchedDocuments = [...documents].sort(
      (a: any, b: any) =>
        new Date(b.Date || 0).getTime() - new Date(a.Date || 0).getTime(),
    );
    matchedDocuments.forEach((doc: any) => {
      const name = doc.Name || "";
      const status = doc.Docstatus || "";
      const type = doc.Type || "";
      const contentStr = Array.isArray(doc.Content)
        ? doc.Content.join(" ")
        : "";

      if (
        fuzzyMatch(name.toLowerCase(), query) ||
        fuzzyMatch(status.toLowerCase(), query) ||
        fuzzyMatch(type.toLowerCase(), query) ||
        contentStr.toLowerCase().includes(query)
      ) {
        let docSnippet = name;
        if (contentStr.toLowerCase().includes(query)) {
          docSnippet = getSnippet(contentStr);
        }

        results.push({
          id: `documents-${doc.Name || doc.Date}`,
          title: docSnippet,
          subtitle: `Documents (Name: ${name})`,
          onClick: () => {
            const idx = tabs.findIndex((t) => t.id === "documents");
            if (idx !== -1) {
              setActiveTab(idx);
              handleOpenDoc(doc);
            }
          },
        });
      }
    });

    // Stable sort: exact/substring title matches first, fuzzy matches after
    return results
      .map((r, i) => ({ r, i, rank: matchRank(r.title) }))
      .sort((a, b) => a.rank - b.rank || a.i - b.i)
      .map(({ r }) => r);
  }, [
    globalSearch,
    Patient,
    fullAddress,
    Encounters,
    Condition,
    Observation,
    MedicationRequest,
    alergy,
    procedure,
    carePlan,
    immunization,
    DiagnosticReport,
    documents,
    getMedicationName,
  ]);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "50vh",
          gap: 2,
        }}
      >
        <CircularProgress size={60} />
        <Typography color="text.secondary">
          Loading patient records...
        </Typography>
      </Box>
    );
  }

  if (error || !patientData) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Alert
          severity="error"
          sx={{ mb: 3, borderRadius: 1, maxWidth: 600, mx: "auto" }}
        >
          {error || "Patient not found"}
        </Alert>
        <Button
          startIcon={<ChevronLeftRounded />}
          onClick={() => navigate("/patients")}
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          Back to Patients
        </Button>
      </Box>
    );
  }

  const hasFullDetails = (() => {
    const recs = Array.isArray(patientData) ? patientData : [patientData];
    return recs.some(
      (r: any) =>
        r.details?.conditions?.length > 0 ||
        r.details?.medications?.length > 0 ||
        r.details?.observations?.length > 0 ||
        r.details?.allergies?.length > 0,
    );
  })();

  const fetchDetailsUrl = PCC_AUTH_URL.replace("state=abc123", `state=fetch-details:${id}`);

  const handleOpenDoc = (doc: any) => {
    setSelectedDoc(doc);
    setIsDocOpen(true);
  };

  const renderDocContent = (content: string[]) => {
    if (!content || content.length === 0) return "No content available.";
    const raw = content[0];

    if (raw.trim().startsWith("<?xml") || raw.trim().startsWith("<")) {
      try {
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(raw, "text/xml");

        const title = xmlDoc.getElementsByTagName("title")[0]?.textContent;

        const sections = xmlDoc.getElementsByTagName("section");
        if (sections.length > 0) {
          return (
            <Box
              sx={{
                bgcolor: "#f8fafc",
                p: { xs: 1.5, md: 3 },
                borderRadius: 2,
              }}
            >
              {title && (
                <Typography
                  variant="h6"
                  gutterBottom
                  fontWeight={900}
                  sx={{
                    mb: 3,
                    color: "#1e293b",
                    textAlign: "center",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    fontSize: "1rem",
                  }}
                >
                  {title}
                </Typography>
              )}
              {Array.from(sections).map((sec, idx) => {
                const secTitle =
                  sec.getElementsByTagName("title")[0]?.textContent;
                const secText = sec.getElementsByTagName("text")[0]?.innerHTML;
                return (
                  <Paper
                    key={idx}
                    elevation={0}
                    sx={{
                      mb: 3,
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "rgba(0,0,0,0.06)",
                      boxShadow: "rgba(149, 157, 165, 0.08) 0px 4px 12px",
                      overflow: "hidden",
                      bgcolor: "#fff",
                    }}
                  >
                    {secTitle && (
                      <Box
                        sx={{
                          p: "12px 20px",
                          background:
                            "linear-gradient(90deg, #f1f5f9 0%, #fff 100%)",
                          borderBottom: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        <Typography
                          variant="caption"
                          fontWeight={800}
                          sx={{
                            color: "#334155",
                            textTransform: "uppercase",
                            letterSpacing: "0.025em",
                            fontSize: "0.75rem",
                          }}
                        >
                          {secTitle}
                        </Typography>
                      </Box>
                    )}
                    {secText && (
                      <Box
                        sx={{
                          p: 2.5,
                          fontSize: "0.85rem",
                          lineHeight: 1.5,
                          color: "#475569",
                          "& table": {
                            width: "100%",
                            borderCollapse: "separate",
                            borderSpacing: 0,
                            mt: 1.5,
                            mb: 1.5,
                            borderRadius: "8px",
                            overflow: "hidden",
                            border: "1px solid",
                            borderColor: "divider",
                          },
                          "& th": {
                            bgcolor: "#475569",
                            color: "#fff",
                            p: "10px 12px",
                            textAlign: "left",
                            fontSize: "0.7rem",
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: "0.05em",
                          },
                          "& td": {
                            p: "10px 12px",
                            borderBottom: "1px solid",
                            borderColor: "rgba(0,0,0,0.06)",
                            verticalAlign: "top",
                            fontSize: "0.8rem",
                            color: "#1e293b",
                          },
                          "& tr:last-child td": {
                            borderBottom: "none",
                          },
                          "& tr:nth-of-type(even) td": {
                            bgcolor: "#f8fafc",
                          },
                          "& p": {
                            mb: 1.5,
                          },
                          "& p:last-child": {
                            mb: 0,
                          },
                        }}
                        dangerouslySetInnerHTML={{ __html: secText }}
                      />
                    )}
                  </Paper>
                );
              })}
            </Box>
          );
        }

        return (
          <pre style={{ whiteSpace: "pre-wrap", fontFamily: "monospace" }}>
            {raw}
          </pre>
        );
      } catch (e) {
        console.error("XML parsing error:", e);
      }
    }

    try {
      const json = JSON.parse(raw);
      return (
        <pre style={{ whiteSpace: "pre-wrap", fontFamily: "monospace" }}>
          {JSON.stringify(json, null, 2)}
        </pre>
      );
    } catch (e) {
      // Not JSON, just render as text
    }

    return <pre style={{ whiteSpace: "pre-wrap" }}>{raw}</pre>;
  };

  return (
    <Box
      sx={{
        height: "90vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        bgcolor: "#f5f7f9",
      }}
    >
      <style>
        {`
          @media print {
            .no-print {
              display: none !important;
            }
            .print-container {
              display: block !important;
              height: auto !important;
              overflow: visible !important;
              background: white !important;
            }
            .print-content {
              padding: 0 !important;
              margin: 0 !important;
              height: auto !important;
              overflow: visible !important;
            }
            /* Reset MUI absolute/fixed positioning for print */
            .MuiBox-root, .MuiPaper-root {
              position: static !important;
              overflow: visible !important;
            }
          }
        `}
      </style>
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 1100,
          bgcolor: "#f5f7f9",
          pb: 2,
          pt: 1,
        }}
        className="no-print"
      >
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 1,
            background: "#FFFFFF",
            boxShadow: "rgba(17, 17, 26, 0.1) 0px 0px 16px",
          }}
        >
          <Stack spacing={1.5}>
            {/* First line: Avatar and Patient Details (Name, Gender, DOB, ID) in one line */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: "primary.main",
                  fontSize: 20,
                  fontWeight: 700,
                  boxShadow: "0 4px 12px rgba(0, 128, 240, 0.2)",
                }}
              >
                {name?.given?.[0]?.[0]}
              </Avatar>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  flexWrap: "wrap",
                  flex: 1,
                }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight={800}
                  color="text.primary"
                  sx={{ letterSpacing: "-0.01em", fontSize: "1.05rem" }}
                >
                  {fullName}
                </Typography>
                <Chip
                  icon={
                    <WcRounded
                      sx={{
                        fontSize: "14px !important",
                        color: "primary.main",
                      }}
                    />
                  }
                  label={Patient?.gender || "N/A"}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.65rem",
                    bgcolor: "rgba(0, 128, 240, 0.08)",
                    color: "primary.main",
                    border: "none",
                    borderRadius: "4px",
                    px: 0.5,
                    height: "22px",
                    textTransform: "capitalize",
                  }}
                />
                <Chip
                  icon={
                    <CakeRounded
                      sx={{
                        fontSize: "14px !important",
                        color: "primary.main",
                      }}
                    />
                  }
                  label={Patient?.birthDate || "N/A"}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.65rem",
                    bgcolor: "rgba(0, 128, 240, 0.08)",
                    color: "primary.main",
                    border: "none",
                    borderRadius: "4px",
                    px: 0.5,
                    height: "22px",
                  }}
                />
                <Chip
                  icon={
                    <PersonRounded
                      sx={{
                        fontSize: "14px !important",
                        color: "primary.main",
                      }}
                    />
                  }
                  label={`ID: ${Patient?.id || "N/A"}`}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.65rem",
                    bgcolor: "rgba(0, 128, 240, 0.08)",
                    color: "primary.main",
                    border: "none",
                    borderRadius: "4px",
                    px: 0.5,
                    height: "22px",
                  }}
                />
                {lastFetchedAt && (
                  <Chip
                    icon={
                      <UpdateRounded
                        sx={{
                          fontSize: "14px !important",
                          color: "primary.main",
                        }}
                      />
                    }
                    label={`Last fetched: ${getRelativeTime(lastFetchedAt)} (${formatUtc(lastFetchedAt)})`}
                    size="small"
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.65rem",
                      bgcolor: "rgba(0, 128, 240, 0.08)",
                      color: "primary.main",
                      border: "none",
                      borderRadius: "4px",
                      px: 0.5,
                      height: "22px",
                    }}
                  />
                )}
              </Box>
            </Box>

            <Divider sx={{ opacity: 0.4 }} />

            {/* Second line: Search button on the left, Tab Dropdown on the right */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <Box sx={{ position: "relative" , gap: 1, display: "flex", alignItems: "center"}}>
                <TextField
                  placeholder="Global search"
                  size="small"
                  variant="outlined"
                  value={globalSearch}
                  onChange={(e) => {
                    setGlobalSearch(e.target.value);
                    setShowSuggestions(true);
                  }}
                  onFocus={() => setShowSuggestions(true)}
                  onBlur={() =>
                    setTimeout(() => setShowSuggestions(false), 200)
                  }
                  sx={{
                    width: 280,
                    bgcolor: "#f1f5f9", // slightly gray background
                    borderRadius: "4px",
                    "& .MuiOutlinedInput-root": {
                      height: "40px",
                      fontSize: "0.9rem",
                      "& fieldset": {
                        borderColor: "rgba(0, 0, 0, 0.15)",
                      },
                      "&:hover fieldset": {
                        borderColor: "primary.main",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "primary.main",
                      },
                    },
                    "& .MuiInputBase-input::placeholder": {
                      color: "#94a3b8", // gray placeholder
                      opacity: 1,
                      fontWeight: 500,
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchRounded
                          sx={{ color: "text.secondary", fontSize: 20 }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />

                {showSuggestions && globalSearch.trim().length > 0 && (
                  <Paper
                    elevation={4}
                    sx={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      width: 450,
                      maxHeight: 350,
                      overflowY: "auto",
                      zIndex: 1300,
                      mt: 1,
                      borderRadius: 1,
                      boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
                      border: "1px solid rgba(0,0,0,0.08)",
                      bgcolor: "#fff",
                    }}
                  >
                    {suggestions.length > 0 ? (
                      suggestions.map((item, index) => (
                        <Box
                          key={item.id}
                          onMouseDown={(e) => {
                            // Prevent blur from firing before click registers
                            e.preventDefault();
                          }}
                          onClick={() => {
                            isProgrammaticNav.current = true;
                            setClinicalStatusFilter("all");
                            setVerificationStatusFilter("all");
                            setMedicationStatusFilter("all");
                            setMedicationIntentFilter("all");
                            setAllergyStatusFilter("all");
                            setAllergyCriticalityFilter("all");
                            setProcedureStatusFilter("all");
                            setImmunizationStatusFilter("all");
                            setDocumentStatusFilter("all");
                            setDocumentTypeFilter("all");

                            item.onClick();
                            setShowSuggestions(false);
                            setGlobalSearch("");
                          }}
                          sx={{
                            p: 1.5,
                            borderBottom: "1px solid rgba(0,0,0,0.04)",
                            cursor: "pointer",
                            transition: "background-color 0.2s",
                            bgcolor: index % 2 === 0 ? "#ffffff" : "#f8fafc",
                            "&:hover": {
                              bgcolor: "#f1f5f9",
                            },
                            "&:last-child": {
                              borderBottom: "none",
                            },
                          }}
                        >
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            color="#475569"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              fontSize: "0.78rem",
                              mb: 0.75,
                            }}
                          >
                            {highlightText(item.title, globalSearch)}
                          </Typography>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Box
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                px: 0.75,
                                py: 0.15,
                                borderRadius: "3px",
                                bgcolor: "rgba(25, 118, 210, 0.08)",
                                color: "primary.main",
                                fontSize: "0.58rem",
                                fontWeight: 800,
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                              }}
                            >
                              Tab - {item.subtitle}
                            </Box>
                          </Box>
                        </Box>
                      ))
                    ) : (
                      <Box sx={{ p: 2, textAlign: "center" }}>
                        <Typography variant="body2" color="text.secondary">
                          {globalSearch.trim().length < MIN_SEARCH_LENGTH
                            ? `Type at least ${MIN_SEARCH_LENGTH} characters to search.`
                            : "No suggestions found."}
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                )}
              {hospiceDetection.found && (
                <Chip
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      Hospice
                      <InfoOutlined sx={{ fontSize: "15px", color: "#7c3aed" }} />
                    </Box>
                  }
                  size="small"
                  onClick={() => setHospiceDialogOpen(true)}
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.75rem",
                    bgcolor: "rgba(124, 58, 237, 0.1)",
                    color: "#7c3aed",
                    border: "1px solid rgba(124, 58, 237, 0.3)",
                    borderRadius: "4px",
                    height: "40px",
                    px: 1,
                    cursor: "pointer",
                    "&:hover": { bgcolor: "rgba(124, 58, 237, 0.18)" },
                  }}
                />
              )}
              {codeStatusDetection.found && (
                <Chip
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      Code Status
                      <InfoOutlined sx={{ fontSize: "15px", color: "#dc2626" }} />
                    </Box>
                  }
                  size="small"
                  onClick={() => setCodeStatusDialogOpen(true)}
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.75rem",
                    bgcolor: "rgba(220, 38, 38, 0.1)",
                    color: "#dc2626",
                    border: "1px solid rgba(220, 38, 38, 0.3)",
                    borderRadius: "4px",
                    height: "40px",
                    px: 1,
                    cursor: "pointer",
                    "&:hover": { bgcolor: "rgba(220, 38, 38, 0.18)" },
                  }}
                />
              )}
              </Box>

             <Box sx={{ gap: 2, display: "flex", flexWrap: "wrap" }}>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleViewText}
                sx={{
                  height: "40px",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  borderRadius: "4px",
                }}
              >
                View text
              </Button>
              {!hasFullDetails && (
                <Button
                  variant="contained"
                  startIcon={<RocketLaunchRounded sx={{ fontSize: "16px" }} />}
                  onClick={() => (window.location.href = fetchDetailsUrl)}
                  sx={{
                    height: "40px",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.85rem",
                    borderRadius: "4px",
                  }}
                >
                  Fetch Full Details
                </Button>
              )}
              {hasFullDetails && (
                <Button
                  variant="outlined"
                  startIcon={<RocketLaunchRounded sx={{ fontSize: "16px" }} />}
                  onClick={() => (window.location.href = fetchDetailsUrl)}
                  sx={{
                    height: "40px",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.85rem",
                    borderRadius: "4px",
                  }}
                >
                  Refresh Data
                </Button>
              )}
                </Box>
            </Box>
          </Stack>
        </Paper>
      </Box>
      {selectedRecordIndex === null && Array.isArray(patientData) && (
        <Box sx={{ p: 0 }}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: "4px",
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Table
              title="Reports"
              headers={[
                { key: "patientName", label: "Patient Name", width: "1fr" },
                { key: "fetchDate", label: "Date Fetched", width: "1fr" },
                { key: "status", label: "Status", width: "1fr" },
                {
                  key: "actions",
                  label: "Actions",
                  width: "120px",
                  align: "right",
                },
              ]}
              data={patientData.map((record: any, index: number) => {
                const p = record.details?.patient || {};
                const nameObj = p.name?.[0];
                const pName = nameObj
                  ? `${nameObj.given?.join(" ")} ${nameObj.family}`
                  : "Unknown Patient";

                return {
                  patientName: pName,
                  fetchDate: record.updatedAt
                    ? formatUtc(record.updatedAt)
                    : record.date
                      ? record.date
                      : "Unknown Date",
                  status: <StatusDot color="#4caf50" label="Success" />,
                  actions: (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setSelectedRecordIndex(index)}
                      sx={{
                        borderRadius: 1,
                        textTransform: "none",
                        fontWeight: 700,
                        fontSize: "0.75rem",
                        px: 2,
                        py: 0.5,
                      }}
                    >
                      View
                    </Button>
                  ),
                };
              })}
              gridTemplateColumns="1.5fr 1fr 1fr 120px"
              variant="default"
            />
          </Paper>
        </Box>
      )}
      <Box
        sx={{
          flex: 1,
          display:
            selectedRecordIndex === null && Array.isArray(patientData)
              ? "none"
              : "flex",
          gap: 0,
          alignItems: "stretch",
          position: "relative",
          overflow: "hidden",
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      >
        {/* Sidebar Area */}
        <Box
          sx={{
            width: 250,
            flexShrink: 0,
            borderRight: "1px solid",
            borderColor: "divider",
            bgcolor: "#fff",
            display: "flex",
            flexDirection: "column",
            overflowY: "auto",
            zIndex: 2,
          }}
        >
          {tabs.map((tab, idx) => {
            const isActive = activeTab === idx;
            let count = 0;
            if (tab.id === "conditions") count = Condition?.length || 0;
            if (tab.id === "observations") count = Observation?.length || 0;
            if (tab.id === "medications")
              count = MedicationRequest?.length || 0;
            if (tab.id === "allergies") count = alergy?.length || 0;
            if (tab.id === "procedures") count = procedure?.length || 0;
            if (tab.id === "careplans") count = carePlan?.length || 0;
            if (tab.id === "immunizations") count = immunization?.length || 0;
            if (tab.id === "reports") count = DiagnosticReport?.length || 0;
            if (tab.id === "documents") count = documents?.length || 0;
            if (tab.id === "advanced-directives")
              count = filteredAdvancedDirectives?.length || 0;
            if (tab.id === "evaluations")
              count = filteredEvaluations?.length || 0;

            return (
              <Box
                key={tab.id}
                onClick={() => setActiveTab(idx)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  py: 2,
                  px: 2.5,
                  cursor: "pointer",
                  bgcolor: isActive
                    ? "rgba(25, 118, 210, 0.08)"
                    : "transparent",
                  color: isActive ? "primary.main" : "text.secondary",
                  borderLeft: "4px solid",
                  borderColor: isActive ? "primary.main" : "transparent",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    bgcolor: isActive
                      ? "rgba(25, 118, 210, 0.12)"
                      : "rgba(0,0,0,0.04)",
                  },
                }}
              >
                {React.cloneElement(tab.icon as React.ReactElement<any>, {
                  sx: {
                    fontSize: 20,
                    color: isActive ? "primary.main" : "text.secondary",
                  },
                })}
                <Typography
                  variant="body2"
                  fontWeight={isActive ? 700 : 500}
                  sx={{ color: isActive ? "primary.main" : "text.primary" }}
                >
                  {tab.label}
                  {count > 0 && ` (${count})`}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* Right Content Area */}
        <Box
          ref={contentScrollRef}
          className="print-content"
          sx={{
            flex: 1,
            minWidth: 0,
            height: "100%",
            overflowY: "auto",
            scrollBehavior: "smooth",
            bgcolor: "#f8f9fa", // Slightly different white for content bg
            p: 0,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 3.5,
              borderRadius: "4px",
              boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
              bgcolor: "#fff",
              border: "1px solid",
              borderColor: "rgba(0,0,0,0.05)",
            }}
          >
            {/* Card Header Row */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2.5,
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              {/* Left Side: Icon, Section Title, and Count */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                {tabs[activeTab]?.icon &&
                  React.cloneElement(
                    tabs[activeTab].icon as React.ReactElement<any>,
                    {
                      sx: { fontSize: 22, color: "primary.main" },
                    },
                  )}
                <Typography variant="h6" fontWeight={800} color="text.primary">
                  {tabs[activeTab]?.label}
                  {activeSection !== "details" && ` (${totalCount})`}
                </Typography>
              </Box>

              {/* Right Side: Local Search & Filter Fields */}
              {activeSection !== "details" && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    flexWrap: "wrap",
                  }}
                >
                  {activeSection === "conditions" && (
                    <>
                      {/* Clinical Status Filter */}
                      <FormControl
                        size="small"
                        variant="outlined"
                        sx={{ minWidth: 150 }}
                      >
                        <InputLabel
                          id="clinical-status-filter-label"
                          sx={{
                            fontSize: "0.75rem",
                            fontWeight: 700,
                            color: "text.secondary",
                            "&.Mui-focused": {
                              color: "primary.main",
                            },
                          }}
                        >
                          Clinical Status
                        </InputLabel>
                        <Select
                          labelId="clinical-status-filter-label"
                          label="Clinical Status"
                          value={clinicalStatusFilter}
                          onChange={(e) =>
                            setClinicalStatusFilter(e.target.value)
                          }
                          sx={{
                            height: "36px",
                            bgcolor: "#f1f5f9",
                            fontSize: "0.75rem",
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "rgba(0, 0, 0, 0.15)",
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: "primary.main",
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "primary.main",
                            },
                          }}
                        >
                          <MenuItem value="all" sx={{ fontSize: "0.75rem" }}>
                            All Statuses
                          </MenuItem>
                          {Array.from(
                            new Set(
                              Condition.map(
                                (c: any) => c.clinicalStatus?.coding?.[0]?.code,
                              ).filter(Boolean),
                            ),
                          ).map((status: any) => (
                            <MenuItem
                              key={status}
                              value={status}
                              sx={{
                                fontSize: "0.75rem",
                                textTransform: "capitalize",
                              }}
                            >
                              {status}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      {/* Verification Status Filter */}
                      <FormControl
                        size="small"
                        variant="outlined"
                        sx={{ minWidth: 160 }}
                      >
                        <InputLabel
                          id="verification-status-filter-label"
                          sx={{
                            fontSize: "0.75rem",
                            fontWeight: 700,
                            color: "text.secondary",
                            "&.Mui-focused": {
                              color: "primary.main",
                            },
                          }}
                        >
                          Verification Status
                        </InputLabel>
                        <Select
                          labelId="verification-status-filter-label"
                          label="Verification Status"
                          value={verificationStatusFilter}
                          onChange={(e) =>
                            setVerificationStatusFilter(e.target.value)
                          }
                          sx={{
                            height: "36px",
                            bgcolor: "#f1f5f9",
                            fontSize: "0.75rem",
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "rgba(0, 0, 0, 0.15)",
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: "primary.main",
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "primary.main",
                            },
                          }}
                        >
                          <MenuItem value="all" sx={{ fontSize: "0.75rem" }}>
                            All Verifications
                          </MenuItem>
                          {Array.from(
                            new Set(
                              Condition.map(
                                (c: any) =>
                                  c.verificationStatus?.coding?.[0]?.code,
                              ).filter(Boolean),
                            ),
                          ).map((status: any) => (
                            <MenuItem
                              key={status}
                              value={status}
                              sx={{
                                fontSize: "0.75rem",
                                textTransform: "capitalize",
                              }}
                            >
                              {status}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </>
                  )}

                  {activeSection === "vitals" && (
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={vitalsLatestOnly}
                          onChange={(e) =>
                            setVitalsLatestOnly(e.target.checked)
                          }
                        />
                      }
                      label="Latest only"
                      sx={{
                        ml: 0,
                        "& .MuiFormControlLabel-label": {
                          fontSize: "0.75rem",
                          fontWeight: 700,
                          color: "text.secondary",
                        },
                      }}
                    />
                  )}

                  {activeSection === "medications" && (
                    <>
                      {/* Medication Status Filter */}
                      <FormControl
                        size="small"
                        variant="outlined"
                        sx={{ minWidth: 150 }}
                      >
                        <InputLabel
                          id="medication-status-filter-label"
                          sx={{
                            fontSize: "0.75rem",
                            fontWeight: 700,
                            color: "text.secondary",
                            "&.Mui-focused": {
                              color: "primary.main",
                            },
                          }}
                        >
                          Status
                        </InputLabel>
                        <Select
                          labelId="medication-status-filter-label"
                          label="Status"
                          value={medicationStatusFilter}
                          onChange={(e) =>
                            setMedicationStatusFilter(e.target.value)
                          }
                          sx={{
                            height: "36px",
                            bgcolor: "#f1f5f9",
                            fontSize: "0.75rem",
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "rgba(0, 0, 0, 0.15)",
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: "primary.main",
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "primary.main",
                            },
                          }}
                        >
                          <MenuItem value="all" sx={{ fontSize: "0.75rem" }}>
                            All
                          </MenuItem>
                          <MenuItem value="active" sx={{ fontSize: "0.75rem" }}>
                            Active
                          </MenuItem>
                          <MenuItem value="inactive" sx={{ fontSize: "0.75rem" }}>
                            Inactive
                          </MenuItem>
                        </Select>
                      </FormControl>

                      {/* Medication Intent Filter */}
                      <FormControl
                        size="small"
                        variant="outlined"
                        sx={{ minWidth: 150 }}
                      >
                        <InputLabel
                          id="medication-intent-filter-label"
                          sx={{
                            fontSize: "0.75rem",
                            fontWeight: 700,
                            color: "text.secondary",
                            "&.Mui-focused": {
                              color: "primary.main",
                            },
                          }}
                        >
                          Intent
                        </InputLabel>
                        <Select
                          labelId="medication-intent-filter-label"
                          label="Intent"
                          value={medicationIntentFilter}
                          onChange={(e) =>
                            setMedicationIntentFilter(e.target.value)
                          }
                          sx={{
                            height: "36px",
                            bgcolor: "#f1f5f9",
                            fontSize: "0.75rem",
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "rgba(0, 0, 0, 0.15)",
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: "primary.main",
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "primary.main",
                            },
                          }}
                        >
                          <MenuItem value="all" sx={{ fontSize: "0.75rem" }}>
                            All Intents
                          </MenuItem>
                          {Array.from(
                            new Set(
                              MedicationRequest.map(
                                (m: any) => m.intent,
                              ).filter(Boolean),
                            ),
                          ).map((intent: any) => (
                            <MenuItem
                              key={intent}
                              value={intent}
                              sx={{
                                fontSize: "0.75rem",
                                textTransform: "capitalize",
                              }}
                            >
                              {intent}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </>
                  )}

                  {activeSection === "allergies" && (
                    <>
                      {/* Allergy Status Filter */}
                      <FormControl
                        size="small"
                        variant="outlined"
                        sx={{ minWidth: 150 }}
                      >
                        <InputLabel
                          id="allergy-status-filter-label"
                          sx={{
                            fontSize: "0.75rem",
                            fontWeight: 700,
                            color: "text.secondary",
                            "&.Mui-focused": {
                              color: "primary.main",
                            },
                          }}
                        >
                          Status
                        </InputLabel>
                        <Select
                          labelId="allergy-status-filter-label"
                          label="Status"
                          value={allergyStatusFilter}
                          onChange={(e) =>
                            setAllergyStatusFilter(e.target.value)
                          }
                          sx={{
                            height: "36px",
                            bgcolor: "#f1f5f9",
                            fontSize: "0.75rem",
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "rgba(0, 0, 0, 0.15)",
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: "primary.main",
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "primary.main",
                            },
                          }}
                        >
                          <MenuItem value="all" sx={{ fontSize: "0.75rem" }}>
                            All Statuses
                          </MenuItem>
                          {Array.from(
                            new Set(
                              alergy
                                .map(
                                  (a: any) =>
                                    a.clinicalStatus?.coding?.[0]?.code,
                                )
                                .filter(Boolean),
                            ),
                          ).map((status: any) => (
                            <MenuItem
                              key={status}
                              value={status}
                              sx={{
                                fontSize: "0.75rem",
                                textTransform: "capitalize",
                              }}
                            >
                              {status}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      {/* Allergy Criticality Filter */}
                      <FormControl
                        size="small"
                        variant="outlined"
                        sx={{ minWidth: 150 }}
                      >
                        <InputLabel
                          id="allergy-criticality-filter-label"
                          sx={{
                            fontSize: "0.75rem",
                            fontWeight: 700,
                            color: "text.secondary",
                            "&.Mui-focused": {
                              color: "primary.main",
                            },
                          }}
                        >
                          Criticality
                        </InputLabel>
                        <Select
                          labelId="allergy-criticality-filter-label"
                          label="Criticality"
                          value={allergyCriticalityFilter}
                          onChange={(e) =>
                            setAllergyCriticalityFilter(e.target.value)
                          }
                          sx={{
                            height: "36px",
                            bgcolor: "#f1f5f9",
                            fontSize: "0.75rem",
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "rgba(0, 0, 0, 0.15)",
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: "primary.main",
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "primary.main",
                            },
                          }}
                        >
                          <MenuItem value="all" sx={{ fontSize: "0.75rem" }}>
                            All Criticalities
                          </MenuItem>
                          {Array.from(
                            new Set(
                              alergy
                                .map((a: any) => a.criticality)
                                .filter(Boolean),
                            ),
                          ).map((crit: any) => (
                            <MenuItem
                              key={crit}
                              value={crit}
                              sx={{
                                fontSize: "0.75rem",
                                textTransform: "capitalize",
                              }}
                            >
                              {crit}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </>
                  )}

                  {activeSection === "procedures" && (
                    <>
                      {/* Procedure Status Filter */}
                      <FormControl
                        size="small"
                        variant="outlined"
                        sx={{ minWidth: 150 }}
                      >
                        <InputLabel
                          id="procedure-status-filter-label"
                          sx={{
                            fontSize: "0.75rem",
                            fontWeight: 700,
                            color: "text.secondary",
                            "&.Mui-focused": {
                              color: "primary.main",
                            },
                          }}
                        >
                          Status
                        </InputLabel>
                        <Select
                          labelId="procedure-status-filter-label"
                          label="Status"
                          value={procedureStatusFilter}
                          onChange={(e) =>
                            setProcedureStatusFilter(e.target.value)
                          }
                          sx={{
                            height: "36px",
                            bgcolor: "#f1f5f9",
                            fontSize: "0.75rem",
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "rgba(0, 0, 0, 0.15)",
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: "primary.main",
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "primary.main",
                            },
                          }}
                        >
                          <MenuItem value="all" sx={{ fontSize: "0.75rem" }}>
                            All Statuses
                          </MenuItem>
                          {Array.from(
                            new Set(
                              procedure
                                .map((p: any) => p.status)
                                .filter(Boolean),
                            ),
                          ).map((status: any) => (
                            <MenuItem
                              key={status}
                              value={status}
                              sx={{
                                fontSize: "0.75rem",
                                textTransform: "capitalize",
                              }}
                            >
                              {status}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </>
                  )}

                  {activeSection === "immunizations" && (
                    <>
                      {/* Immunization Status Filter */}
                      <FormControl
                        size="small"
                        variant="outlined"
                        sx={{ minWidth: 150 }}
                      >
                        <InputLabel
                          id="immunization-status-filter-label"
                          sx={{
                            fontSize: "0.75rem",
                            fontWeight: 700,
                            color: "text.secondary",
                            "&.Mui-focused": {
                              color: "primary.main",
                            },
                          }}
                        >
                          Status
                        </InputLabel>
                        <Select
                          labelId="immunization-status-filter-label"
                          label="Status"
                          value={immunizationStatusFilter}
                          onChange={(e) =>
                            setImmunizationStatusFilter(e.target.value)
                          }
                          sx={{
                            height: "36px",
                            bgcolor: "#f1f5f9",
                            fontSize: "0.75rem",
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "rgba(0, 0, 0, 0.15)",
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: "primary.main",
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "primary.main",
                            },
                          }}
                        >
                          <MenuItem value="all" sx={{ fontSize: "0.75rem" }}>
                            All Statuses
                          </MenuItem>
                          {Array.from(
                            new Set(
                              immunization
                                .map((i: any) => i.status)
                                .filter(Boolean),
                            ),
                          ).map((status: any) => (
                            <MenuItem
                              key={status}
                              value={status}
                              sx={{
                                fontSize: "0.75rem",
                                textTransform: "capitalize",
                              }}
                            >
                              {status}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </>
                  )}

                  {activeSection === "documents" && (
                    <>
                      {/* Document Status Filter */}
                      <FormControl
                        size="small"
                        variant="outlined"
                        sx={{ minWidth: 150 }}
                      >
                        <InputLabel
                          id="document-status-filter-label"
                          sx={{
                            fontSize: "0.75rem",
                            fontWeight: 700,
                            color: "text.secondary",
                            "&.Mui-focused": {
                              color: "primary.main",
                            },
                          }}
                        >
                          Status
                        </InputLabel>
                        <Select
                          labelId="document-status-filter-label"
                          label="Status"
                          value={documentStatusFilter}
                          onChange={(e) =>
                            setDocumentStatusFilter(e.target.value)
                          }
                          sx={{
                            height: "36px",
                            bgcolor: "#f1f5f9",
                            fontSize: "0.75rem",
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "rgba(0, 0, 0, 0.15)",
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: "primary.main",
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "primary.main",
                            },
                          }}
                        >
                          <MenuItem value="all" sx={{ fontSize: "0.75rem" }}>
                            All Statuses
                          </MenuItem>
                          {Array.from(
                            new Set(
                              documents
                                .map((d: any) => d.Docstatus)
                                .filter(Boolean),
                            ),
                          ).map((status: any) => (
                            <MenuItem
                              key={status}
                              value={status}
                              sx={{
                                fontSize: "0.75rem",
                                textTransform: "capitalize",
                              }}
                            >
                              {status}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      {/* Document Type Filter */}
                      <FormControl
                        size="small"
                        variant="outlined"
                        sx={{ minWidth: 150 }}
                      >
                        <InputLabel
                          id="document-type-filter-label"
                          sx={{
                            fontSize: "0.75rem",
                            fontWeight: 700,
                            color: "text.secondary",
                            "&.Mui-focused": {
                              color: "primary.main",
                            },
                          }}
                        >
                          Type
                        </InputLabel>
                        <Select
                          labelId="document-type-filter-label"
                          label="Type"
                          value={documentTypeFilter}
                          onChange={(e) =>
                            setDocumentTypeFilter(e.target.value)
                          }
                          sx={{
                            height: "36px",
                            bgcolor: "#f1f5f9",
                            fontSize: "0.75rem",
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "rgba(0, 0, 0, 0.15)",
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: "primary.main",
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "primary.main",
                            },
                          }}
                        >
                          <MenuItem value="all" sx={{ fontSize: "0.75rem" }}>
                            All Types
                          </MenuItem>
                          {Array.from(
                            new Set(
                              documents.map((d: any) => d.Type).filter(Boolean),
                            ),
                          ).map((type: any) => (
                            <MenuItem
                              key={type}
                              value={type}
                              sx={{
                                fontSize: "0.75rem",
                                textTransform: "lowercase",
                              }}
                            >
                              {type}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </>
                  )}

                  {activeSection !== "advanced-directives" &&
                    activeSection !== "evaluations" && (
                      <TextField
                        placeholder={`Search ${tabs[activeTab]?.label.toLowerCase()}...`}
                        size="small"
                        variant="outlined"
                        value={localSearch}
                        onChange={(e) => setLocalSearch(e.target.value)}
                        sx={{
                          width: 240,
                          bgcolor: "#f1f5f9",
                          borderRadius: "4px",
                          "& .MuiOutlinedInput-root": {
                            height: "36px",
                            fontSize: "0.85rem",
                            "& fieldset": {
                              borderColor: "rgba(0, 0, 0, 0.15)",
                            },
                            "&:hover fieldset": {
                              borderColor: "primary.main",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "primary.main",
                            },
                          },
                          "& .MuiInputBase-input::placeholder": {
                            color: "#94a3b8",
                            opacity: 1,
                            fontWeight: 500,
                          },
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchRounded
                                sx={{ color: "text.secondary", fontSize: 18 }}
                              />
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                </Box>
              )}
            </Box>

            <Divider sx={{ mb: 3 }} />

            <Stack spacing={4}>
              {/* Patient Details Section */}
              {activeSection === "details" && (
                <Box id="details">
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 3,
                    }}
                  >
                    {/* Left Column: Demographics & Identity */}
                    <Box>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 3,
                          borderRadius: 3,
                          border: "1px solid rgba(0,0,0,0.06)",
                          boxShadow: "0 4px 20px rgba(0,0,0,0.03)",
                          bgcolor: "#fff",
                          height: "100%",
                        }}
                      >
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={1.5}
                          mb={2.5}
                        >
                          <PersonRounded
                            sx={{ color: "primary.main", fontSize: 24 }}
                          />
                          <Typography
                            variant="h6"
                            fontWeight={800}
                            color="text.primary"
                          >
                            Demographics & Identity
                          </Typography>
                        </Box>
                        <Stack spacing={2.5}>
                          {/* Dynamic Names */}
                          {Patient?.name && Patient.name.length > 0 && (
                            <Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                fontWeight={700}
                                sx={{
                                  textTransform: "uppercase",
                                  fontSize: "0.68rem",
                                  letterSpacing: "0.5px",
                                }}
                              >
                                Patient Names ({Patient.name.length})
                              </Typography>
                              <Stack spacing={1} sx={{ mt: 0.5 }}>
                                {Patient.name.map((n: any, idx: number) => (
                                  <Box
                                    key={idx}
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                    flexWrap="wrap"
                                  >
                                    <Typography
                                      variant="body2"
                                      fontWeight={700}
                                      color="text.primary"
                                    >
                                      {n.given?.join(" ")} {n.family}
                                    </Typography>
                                    {n.use && (
                                      <Chip
                                        label={n.use}
                                        size="small"
                                        sx={{
                                          height: 18,
                                          fontSize: "0.65rem",
                                          fontWeight: 700,
                                          textTransform: "uppercase",
                                          bgcolor:
                                            n.use === "official"
                                              ? "rgba(0, 128, 240, 0.08)"
                                              : "rgba(0, 0, 0, 0.04)",
                                          color:
                                            n.use === "official"
                                              ? "primary.main"
                                              : "text.secondary",
                                          borderRadius: "4px",
                                        }}
                                      />
                                    )}
                                  </Box>
                                ))}
                              </Stack>
                            </Box>
                          )}

                          {/* Date of Birth & Age */}
                          <Box display="flex" gap={4} flexWrap="wrap">
                            <Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                fontWeight={700}
                                sx={{
                                  textTransform: "uppercase",
                                  fontSize: "0.68rem",
                                  letterSpacing: "0.5px",
                                  display: "block",
                                }}
                              >
                                Date of Birth
                              </Typography>
                              <Box
                                display="flex"
                                alignItems="center"
                                gap={1}
                                sx={{ mt: 0.5 }}
                              >
                                <CakeRounded
                                  sx={{ fontSize: 16, color: "text.secondary" }}
                                />
                                <Typography variant="body2" fontWeight={700}>
                                  {Patient?.birthDate || "N/A"}
                                </Typography>
                                {Patient?.birthDate && (
                                  <Chip
                                    label={`${getAge(Patient.birthDate)} Yrs Old`}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    sx={{
                                      height: 20,
                                      fontSize: "0.7rem",
                                      fontWeight: 700,
                                    }}
                                  />
                                )}
                              </Box>
                            </Box>

                            <Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                fontWeight={700}
                                sx={{
                                  textTransform: "uppercase",
                                  fontSize: "0.68rem",
                                  letterSpacing: "0.5px",
                                  display: "block",
                                }}
                              >
                                Administrative Gender
                              </Typography>
                              <Box
                                display="flex"
                                alignItems="center"
                                gap={1}
                                sx={{ mt: 0.5 }}
                              >
                                <WcRounded
                                  sx={{ fontSize: 16, color: "text.secondary" }}
                                />
                                <Typography
                                  variant="body2"
                                  fontWeight={700}
                                  sx={{ textTransform: "capitalize" }}
                                >
                                  {Patient?.gender || "N/A"}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>

                          <Divider />

                          {/* US Core Extensions */}
                          <Box display="flex" flexDirection="column" gap={2}>
                            {/* Birth Sex */}
                            {getBirthsex(Patient) && (
                              <Box
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                              >
                                <Typography
                                  variant="body2"
                                  fontWeight={500}
                                  color="text.secondary"
                                >
                                  Birth Sex
                                </Typography>
                                <Chip
                                  label={getBirthsex(Patient)}
                                  size="small"
                                  sx={{
                                    fontWeight: 700,
                                    bgcolor: "rgba(0, 128, 240, 0.08)",
                                    color: "primary.main",
                                  }}
                                />
                              </Box>
                            )}

                            {/* Gender Identity */}
                            {getGenderIdentity(Patient) && (
                              <Box
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                              >
                                <Typography
                                  variant="body2"
                                  fontWeight={500}
                                  color="text.secondary"
                                >
                                  Gender Identity
                                </Typography>
                                <Typography
                                  variant="body2"
                                  fontWeight={700}
                                  color="text.primary"
                                >
                                  {getGenderIdentity(Patient)}
                                </Typography>
                              </Box>
                            )}

                            {/* Race */}
                            {getRace(Patient) && (
                              <Box
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                              >
                                <Typography
                                  variant="body2"
                                  fontWeight={500}
                                  color="text.secondary"
                                >
                                  Race
                                </Typography>
                                <Typography
                                  variant="body2"
                                  fontWeight={700}
                                  color="text.primary"
                                >
                                  {getRace(Patient)}
                                </Typography>
                              </Box>
                            )}

                            {/* Languages */}
                            {Patient?.communication &&
                              Patient.communication.length > 0 && (
                                <Box>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    fontWeight={700}
                                    sx={{
                                      textTransform: "uppercase",
                                      fontSize: "0.68rem",
                                      letterSpacing: "0.5px",
                                      display: "block",
                                      mb: 0.5,
                                    }}
                                  >
                                    Languages / Communication
                                  </Typography>
                                  <Box display="flex" gap={1} flexWrap="wrap">
                                    {Patient.communication.map(
                                      (c: any, idx: number) => {
                                        const langDisplay =
                                          c.language?.coding?.[0]?.display;
                                        const langText = c.language?.text;
                                        let langName =
                                          langDisplay ||
                                          langText ||
                                          c.language?.coding?.[0]?.code ||
                                          "Unknown";
                                        if (
                                          langDisplay &&
                                          langText &&
                                          langText !== langDisplay
                                        ) {
                                          langName = `${langDisplay} (${langText})`;
                                        }
                                        const isPreferred =
                                          c.preferred === true;
                                        return (
                                          <Chip
                                            key={idx}
                                            icon={
                                              <TranslateRounded
                                                sx={{
                                                  fontSize: "14px !important",
                                                }}
                                              />
                                            }
                                            label={
                                              langName +
                                              (isPreferred
                                                ? " (Preferred)"
                                                : "")
                                            }
                                            size="small"
                                            variant="outlined"
                                            sx={{
                                              fontWeight: 600,
                                              fontSize: "0.75rem",
                                            }}
                                          />
                                        );
                                      },
                                    )}
                                  </Box>
                                </Box>
                              )}
                          </Box>
                        </Stack>
                      </Paper>
                    </Box>

                    {/* Right Column: Contact & Location Card + Security & Metadata Card */}
                    <Box display="flex" flexDirection="column" gap={3}>
                      {/* Card 2: Contact & Location */}
                      <Paper
                        elevation={0}
                        sx={{
                          p: 3,
                          borderRadius: 3,
                          border: "1px solid rgba(0,0,0,0.06)",
                          boxShadow: "0 4px 20px rgba(0,0,0,0.03)",
                          bgcolor: "#fff",
                        }}
                      >
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={1.5}
                          mb={2.5}
                        >
                          <LocationOnRounded
                            sx={{ color: "primary.main", fontSize: 24 }}
                          />
                          <Typography
                            variant="h6"
                            fontWeight={800}
                            color="text.primary"
                          >
                            Contact & Locations
                          </Typography>
                        </Box>
                        <Stack spacing={3}>
                          {/* Telecoms */}
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              fontWeight={700}
                              sx={{
                                textTransform: "uppercase",
                                fontSize: "0.68rem",
                                letterSpacing: "0.5px",
                                display: "block",
                                mb: 1,
                              }}
                            >
                              Telecom Information
                            </Typography>
                            {Patient?.telecom && Patient.telecom.length > 0 ? (
                              <Stack spacing={1.5}>
                                {Patient.telecom.map((t: any, idx: number) => (
                                  <Box
                                    key={idx}
                                    display="flex"
                                    alignItems="center"
                                    gap={1.5}
                                  >
                                    <Box
                                      sx={{
                                        width: 28,
                                        height: 28,
                                        borderRadius: "50%",
                                        bgcolor:
                                          t.system === "email"
                                            ? "rgba(244, 63, 94, 0.08)"
                                            : "rgba(0, 128, 240, 0.08)",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color:
                                          t.system === "email"
                                            ? "#f43f5e"
                                            : "primary.main",
                                      }}
                                    >
                                      {t.system === "email" ? (
                                        <MailRounded sx={{ fontSize: 14 }} />
                                      ) : (
                                        <CallRounded sx={{ fontSize: 14 }} />
                                      )}
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                      <Box
                                        display="flex"
                                        alignItems="center"
                                        gap={1}
                                      >
                                        <Typography
                                          variant="body2"
                                          fontWeight={700}
                                          color="text.primary"
                                        >
                                          {t.value}
                                        </Typography>
                                        {t.use && (
                                          <Chip
                                            label={t.use}
                                            size="small"
                                            sx={{
                                              height: 16,
                                              fontSize: "0.65rem",
                                              fontWeight: 700,
                                              textTransform: "uppercase",
                                              bgcolor: "rgba(0, 0, 0, 0.04)",
                                              color: "text.secondary",
                                              borderRadius: "4px",
                                            }}
                                          />
                                        )}
                                      </Box>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ textTransform: "capitalize" }}
                                      >
                                        {t.system || "phone"}
                                      </Typography>
                                    </Box>
                                  </Box>
                                ))}
                              </Stack>
                            ) : (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                No telecom data recorded
                              </Typography>
                            )}
                          </Box>

                          <Divider />

                          {/* Addresses */}
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              fontWeight={700}
                              sx={{
                                textTransform: "uppercase",
                                fontSize: "0.68rem",
                                letterSpacing: "0.5px",
                                display: "block",
                                mb: 1,
                              }}
                            >
                              Addresses
                            </Typography>
                            {Patient?.address && Patient.address.length > 0 ? (
                              <Stack spacing={2}>
                                {Patient.address.map((a: any, idx: number) => {
                                  const addrStr = `${a.line?.join(", ")}, ${a.city || ""}, ${a.state || ""} ${a.postalCode || ""}${a.country ? `, ${a.country}` : ""}`;
                                  return (
                                    <Box
                                      key={idx}
                                      sx={{
                                        p: 1.5,
                                        borderRadius: 2,
                                        bgcolor: "rgba(0,0,0,0.02)",
                                        border: "1px solid rgba(0,0,0,0.03)",
                                      }}
                                    >
                                      <Box
                                        display="flex"
                                        alignItems="flex-start"
                                        justifyContent="space-between"
                                        gap={1}
                                        mb={0.5}
                                      >
                                        <Typography
                                          variant="body2"
                                          fontWeight={700}
                                          color="text.primary"
                                        >
                                          {addrStr}
                                        </Typography>
                                        {a.use && (
                                          <Chip
                                            label={a.use}
                                            size="small"
                                            color={
                                              a.use === "home" ||
                                              a.use === "current"
                                                ? "success"
                                                : "default"
                                            }
                                            variant={
                                              a.use === "home" ||
                                              a.use === "current"
                                                ? "filled"
                                                : "outlined"
                                            }
                                            sx={{
                                              height: 18,
                                              fontSize: "0.65rem",
                                              fontWeight: 700,
                                              textTransform: "uppercase",
                                              borderRadius: "4px",
                                            }}
                                          />
                                        )}
                                      </Box>
                                      {a.district && (
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          display="block"
                                        >
                                          District: {a.district}
                                        </Typography>
                                      )}
                                      {a.period &&
                                        (a.period.start || a.period.end) && (
                                          <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{
                                              display: "block",
                                              mt: 0.5,
                                              fontStyle: "italic",
                                            }}
                                          >
                                            Period:{" "}
                                            {a.period.start
                                              ? new Date(
                                                  a.period.start,
                                                ).toLocaleDateString()
                                              : ""}{" "}
                                            -{" "}
                                            {a.period.end
                                              ? new Date(
                                                  a.period.end,
                                                ).toLocaleDateString()
                                              : "Present"}
                                          </Typography>
                                        )}
                                    </Box>
                                  );
                                })}
                              </Stack>
                            ) : (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                No addresses recorded
                              </Typography>
                            )}
                          </Box>
                        </Stack>
                      </Paper>

                      {/* Card 3: Security & FHIR Metadata */}
                      <Paper
                        elevation={0}
                        sx={{
                          p: 3,
                          borderRadius: 3,
                          border: "1px solid rgba(0,0,0,0.06)",
                          boxShadow: "0 4px 20px rgba(0,0,0,0.03)",
                          bgcolor: "#fff",
                        }}
                      >
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={1.5}
                          mb={2.5}
                        >
                          <SecurityRounded
                            sx={{ color: "primary.main", fontSize: 24 }}
                          />
                          <Typography
                            variant="h6"
                            fontWeight={800}
                            color="text.primary"
                          >
                            Security & FHIR Metadata
                          </Typography>
                        </Box>
                        <Stack spacing={2.5}>
                          {/* Last Updated & Version */}
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            gap={2}
                            flexWrap="wrap"
                          >
                            <Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                fontWeight={700}
                                sx={{
                                  textTransform: "uppercase",
                                  fontSize: "0.68rem",
                                  letterSpacing: "0.5px",
                                  display: "block",
                                }}
                              >
                                Last Updated
                              </Typography>
                              <Typography
                                variant="body2"
                                fontWeight={700}
                                sx={{ mt: 0.5 }}
                              >
                                {Patient?.meta?.lastUpdated
                                  ? new Date(
                                      Patient.meta.lastUpdated,
                                    ).toLocaleString()
                                  : "N/A"}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                fontWeight={700}
                                sx={{
                                  textTransform: "uppercase",
                                  fontSize: "0.68rem",
                                  letterSpacing: "0.5px",
                                  display: "block",
                                }}
                              >
                                Version ID
                              </Typography>
                              <Typography
                                variant="body2"
                                fontWeight={700}
                                sx={{ mt: 0.5 }}
                              >
                                {Patient?.meta?.versionId || "1"}
                              </Typography>
                            </Box>
                          </Box>

                          {/* Security Labels */}
                          {Patient?.meta?.security &&
                            Patient.meta.security.length > 0 && (
                              <Box>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  fontWeight={700}
                                  sx={{
                                    textTransform: "uppercase",
                                    fontSize: "0.68rem",
                                    letterSpacing: "0.5px",
                                    display: "block",
                                    mb: 0.5,
                                  }}
                                >
                                  Security Labels / Tags
                                </Typography>
                                <Box display="flex" gap={1} flexWrap="wrap">
                                  {Patient.meta.security.map(
                                    (sec: any, idx: number) => (
                                      <Chip
                                        key={idx}
                                        label={sec.display || sec.code}
                                        size="small"
                                        variant="outlined"
                                        sx={{
                                          fontWeight: 600,
                                          fontSize: "0.75rem",
                                        }}
                                      />
                                    ),
                                  )}
                                </Box>
                              </Box>
                            )}
                        </Stack>
                      </Paper>
                    </Box>
                  </Box>
                </Box>
              )}

              {/* Encounters Section */}
              {activeSection === "encounters" && (
                <Box id="encounters">
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 3,
                    }}
                  >
                    {filteredEncounters.length > 0 ? (
                      filteredEncounters.map((enc: any, index: number) => (
                        <Paper
                          key={index}
                          elevation={0}
                          sx={{
                            p: 2.5,
                            borderRadius: 2,
                            boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
                            bgcolor: "#fff",
                            border: "1px solid",
                            borderColor: "rgba(0,0,0,0.05)",
                          }}
                        >
                          <Box display="flex" gap={3} alignItems="flex-start">
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: "12px",
                                bgcolor: "rgba(0, 128, 240, 0.08)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "primary.main",
                                flexShrink: 0,
                                boxShadow: "0 4px 12px rgba(0, 128, 240, 0.1)",
                              }}
                            >
                              <HistoryEduRounded sx={{ fontSize: 22 }} />
                            </Box>

                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "flex-start",
                                  gap: 2,
                                  mb: 1.5,
                                }}
                              >
                                <Typography
                                  variant="body1"
                                  fontWeight={800}
                                  color="text.primary"
                                  sx={{ fontSize: "1.1rem", lineHeight: 1.3 }}
                                >
                                  {enc.type?.[0]?.coding?.[0]?.display ||
                                    "Encounter"}
                                </Typography>
                                <Chip
                                  label={enc.status}
                                  size="small"
                                  color={
                                    enc.status === "finished"
                                      ? "success"
                                      : "warning"
                                  }
                                  sx={{
                                    height: 24,
                                    px: 1,
                                    fontSize: "0.7rem",
                                    fontWeight: 800,
                                    textTransform: "uppercase",
                                    borderRadius: "6px",
                                  }}
                                />
                              </Box>

                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 3,
                                  mb: 2.5,
                                  flexWrap: "wrap",
                                }}
                              >
                                <Box>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: "text.secondary",
                                      fontWeight: 700,
                                      display: "block",
                                      mb: 0.2,
                                      textTransform: "uppercase",
                                      fontSize: "0.65rem",
                                      letterSpacing: "0.5px",
                                    }}
                                  >
                                    Start Time
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    fontWeight={600}
                                    color="text.primary"
                                  >
                                    {new Date(enc.start).toLocaleString()}
                                  </Typography>
                                </Box>
                                {enc.end && (
                                  <Box>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: "text.secondary",
                                        fontWeight: 700,
                                        display: "block",
                                        mb: 0.2,
                                        textTransform: "uppercase",
                                        fontSize: "0.65rem",
                                        letterSpacing: "0.5px",
                                      }}
                                    >
                                      End Time
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      fontWeight={600}
                                      color="text.primary"
                                    >
                                      {new Date(enc.end).toLocaleString()}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>

                              <Divider sx={{ mb: 2.5, opacity: 0.6 }} />

                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 3,
                                }}
                              >
                                {enc.conditions &&
                                  enc.conditions.length > 0 && (
                                    <Box sx={{ minWidth: 0 }}>
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          color: "text.secondary",
                                          fontWeight: 700,
                                          mb: 1.5,
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 1,
                                          fontSize: "0.75rem",
                                          textTransform: "uppercase",
                                          letterSpacing: "1px",
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            width: 4,
                                            height: 12,
                                            bgcolor: "primary.main",
                                            borderRadius: 1,
                                          }}
                                        />
                                        Associated Conditions
                                      </Typography>
                                      <Stack
                                        direction="row"
                                        flexWrap="wrap"
                                        gap={1}
                                        sx={{ width: "100%" }}
                                      >
                                        {enc.conditions.map(
                                          (c: any, i: number) => {
                                            const conditionName =
                                              c.code?.coding?.[0]?.display ||
                                              c.code?.text ||
                                              "Condition";
                                            const icdCode =
                                              c.code?.coding?.find(
                                                (cd: any) =>
                                                  cd.system
                                                    ?.toLowerCase()
                                                    .includes("icd"),
                                              )?.code;
                                            return (
                                              <Chip
                                                key={i}
                                                label={
                                                  icdCode
                                                    ? `${conditionName} (${icdCode})`
                                                    : conditionName
                                                }
                                                size="small"
                                                sx={{
                                                  height: 28,
                                                  bgcolor:
                                                    "rgba(0, 128, 240, 0.04)",
                                                  color: "primary.dark",
                                                  fontWeight: 600,
                                                  fontSize: "0.75rem",
                                                  border:
                                                    "1px solid rgba(0, 128, 240, 0.1)",
                                                  maxWidth: "100%",
                                                  "& .MuiChip-label": {
                                                    px: 1.5,
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                  },
                                                }}
                                              />
                                            );
                                          },
                                        )}
                                      </Stack>
                                    </Box>
                                  )}

                                {enc.observations &&
                                  enc.observations.length > 0 && (
                                    <Box sx={{ minWidth: 0 }}>
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          color: "text.secondary",
                                          fontWeight: 700,
                                          mb: 1.5,
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 1,
                                          fontSize: "0.75rem",
                                          textTransform: "uppercase",
                                          letterSpacing: "1px",
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            width: 4,
                                            height: 12,
                                            bgcolor: "success.main",
                                            borderRadius: 1,
                                          }}
                                        />
                                        Key Observations
                                      </Typography>
                                      <Stack
                                        direction="row"
                                        flexWrap="wrap"
                                        gap={1}
                                        sx={{ width: "100%" }}
                                      >
                                        {enc.observations.map(
                                          (o: any, i: number) => (
                                            <Chip
                                              key={i}
                                              label={
                                                o.code?.coding?.[0]?.display ||
                                                "Observation"
                                              }
                                              variant="outlined"
                                              size="small"
                                              sx={{
                                                height: 28,
                                                fontSize: "0.75rem",
                                                fontWeight: 600,
                                                borderColor: "rgba(0,0,0,0.1)",
                                                maxWidth: "100%",
                                                "& .MuiChip-label": {
                                                  px: 1.5,
                                                  overflow: "hidden",
                                                  textOverflow: "ellipsis",
                                                },
                                              }}
                                            />
                                          ),
                                        )}
                                      </Stack>
                                    </Box>
                                  )}
                              </Box>
                            </Box>
                          </Box>
                        </Paper>
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {localSearch
                          ? "No encounters match your search."
                          : "No encounters available."}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}

              {/* Conditions Section */}
              {activeSection === "conditions" && (
                <Box id="conditions">
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                    }}
                  >
                    {filteredConditions.length > 0 ? (
                      filteredConditions.map((c: any, index: number) => {
                        const icd10Code = c.code?.coding?.find((cd: any) =>
                          cd.system?.toLowerCase().includes("icd-10"),
                        )?.code;
                        const icd9Code = c.code?.coding?.find((cd: any) =>
                          cd.system?.toLowerCase().includes("icd-9"),
                        )?.code;
                        return (
                          <Paper
                            key={index}
                            elevation={0}
                            sx={{
                              p: 2.5,
                              borderRadius: 2,
                              boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
                              bgcolor: "#fff",
                              border: "1px solid",
                              borderColor: "rgba(0,0,0,0.05)",
                            }}
                          >
                            <Box
                              display="flex"
                              justifyContent="space-between"
                              alignItems="flex-start"
                            >
                              <Box display="flex" gap={2}>
                                <Box
                                  sx={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: "50%",
                                    bgcolor: "rgba(0, 0, 0, 0.04)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: "text.secondary",
                                  }}
                                >
                                  <AssignmentRounded sx={{ fontSize: 18 }} />
                                </Box>
                                <Box>
                                  <Typography
                                    variant="body1"
                                    fontWeight={700}
                                    color="text.primary"
                                    sx={{ fontSize: "0.95rem" }}
                                  >
                                    {c.code?.text ||
                                      c.code?.coding?.[0]?.display ||
                                      "Unnamed Condition"}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    fontWeight={500}
                                    sx={{ mt: 1 }}
                                  >
                                    Clinical Status:{" "}
                                    <Box
                                      component="span"
                                      sx={{ fontWeight: 700 }}
                                    >
                                      {c.clinicalStatus?.coding?.[0]?.code ||
                                        "N/A"}
                                    </Box>
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    fontWeight={500}
                                  >
                                    Verification Status:{" "}
                                    <Box
                                      component="span"
                                      sx={{ fontWeight: 700 }}
                                    >
                                      {c.verificationStatus?.coding?.[0]
                                        ?.code || "N/A"}
                                    </Box>
                                  </Typography>
                                </Box>
                              </Box>
                              <Chip
                                label={
                                  c.category?.[0]?.coding?.[0]?.display ||
                                  "Condition"
                                }
                                size="small"
                                sx={{
                                  fontWeight: 700,
                                  fontSize: "0.65rem",
                                  bgcolor: "rgba(0, 128, 240, 0.08)",
                                  color: "primary.main",
                                  border: "none",
                                  borderRadius: "4px",
                                }}
                              />
                            </Box>
                            <Box
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                              sx={{ mt: 2 }}
                            >
                              {c.recordedDate ? (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  fontWeight={500}
                                >
                                  Recorded on:{" "}
                                  <Box
                                    component="span"
                                    sx={{ fontWeight: 700 }}
                                  >
                                    {new Date(
                                      c.recordedDate,
                                    ).toLocaleDateString()}
                                  </Box>
                                </Typography>
                              ) : (
                                <Box />
                              )}
                              <Stack direction="row" gap={1} flexWrap="wrap">
                                {icd10Code && (
                                  <Chip
                                    label={`ICD-10: ${icd10Code}`}
                                    size="small"
                                    sx={{
                                      fontWeight: 700,
                                      fontSize: "0.65rem",
                                      bgcolor: "rgba(16, 185, 129, 0.08)",
                                      color: "#10b981",
                                      border: "none",
                                      borderRadius: "4px",
                                    }}
                                  />
                                )}
                                {icd9Code && (
                                  <Chip
                                    label={`ICD-9: ${icd9Code}`}
                                    size="small"
                                    sx={{
                                      fontWeight: 700,
                                      fontSize: "0.65rem",
                                      bgcolor: "rgba(16, 185, 129, 0.08)",
                                      color: "#10b981",
                                      border: "none",
                                      borderRadius: "4px",
                                    }}
                                  />
                                )}
                              </Stack>
                            </Box>
                          </Paper>
                        );
                      })
                    ) : (
                      <Typography color="text.secondary">
                        {localSearch
                          ? "No conditions match your search."
                          : "No conditions found for this patient."}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}

              {/* Fall History Section */}
              {activeSection === "fall-history" && (
                <Box id="fall-history">
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                    }}
                  >
                    {fallHistoryConditions.length > 0 ? (
                      fallHistoryConditions.map((c: any, index: number) => {
                        const matchedCode = c.code?.coding?.find((cd: any) =>
                          FALL_HISTORY_ICD_CODES.includes(
                            (cd.code || "").toLowerCase(),
                          ),
                        )?.code;
                        return (
                          <Paper
                            key={index}
                            elevation={0}
                            sx={{
                              p: 2.5,
                              borderRadius: 2,
                              boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
                              bgcolor: "#fff",
                              border: "1px solid",
                              borderColor: "rgba(0,0,0,0.05)",
                            }}
                          >
                            <Box
                              display="flex"
                              justifyContent="space-between"
                              alignItems="flex-start"
                            >
                              <Box display="flex" gap={2}>
                                <Box
                                  sx={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: "50%",
                                    bgcolor: "rgba(0, 0, 0, 0.04)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: "text.secondary",
                                  }}
                                >
                                  <ElderlyRounded sx={{ fontSize: 18 }} />
                                </Box>
                                <Box>
                                  <Typography
                                    variant="body1"
                                    fontWeight={700}
                                    color="text.primary"
                                    sx={{ fontSize: "0.95rem" }}
                                  >
                                    {c.code?.text ||
                                      c.code?.coding?.[0]?.display ||
                                      "Fall History"}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    fontWeight={500}
                                    sx={{ mt: 1 }}
                                  >
                                    Clinical Status:{" "}
                                    <Box
                                      component="span"
                                      sx={{ fontWeight: 700 }}
                                    >
                                      {c.clinicalStatus?.coding?.[0]?.code ||
                                        "N/A"}
                                    </Box>
                                  </Typography>
                                  {(c.recordedDate || c.onsetDateTime) && (
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      fontWeight={500}
                                    >
                                      Recorded on:{" "}
                                      <Box
                                        component="span"
                                        sx={{ fontWeight: 700 }}
                                      >
                                        {new Date(
                                          c.recordedDate || c.onsetDateTime,
                                        ).toLocaleDateString()}
                                      </Box>
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                              <Chip
                                label={matchedCode ? `Code: ${matchedCode}` : "Fall History"}
                                size="small"
                                sx={{
                                  fontWeight: 700,
                                  fontSize: "0.65rem",
                                  bgcolor: "rgba(0, 128, 240, 0.08)",
                                  color: "primary.main",
                                  border: "none",
                                  borderRadius: "4px",
                                }}
                              />
                            </Box>
                          </Paper>
                        );
                      })
                    ) : (
                      <Typography color="text.secondary">
                        {localSearch
                          ? "No fall history matches your search."
                          : "No fall history found for this patient."}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}

              {/* Vital Signs Section */}
              {activeSection === "vitals" && (
                <Box id="vitals">
                  {Object.keys(groupedVitalSigns).length > 0 ? (
                    Object.entries(groupedVitalSigns).map(
                      ([groupName, obs]) => (
                        <Box key={groupName} sx={{ mb: 3 }}>
                          <Typography
                            variant="subtitle1"
                            fontWeight={700}
                            sx={{ mb: 1.5 }}
                          >
                            {groupName}
                          </Typography>
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: {
                                xs: "1fr",
                                sm: "1fr 1fr",
                                md: "1fr 1fr 1fr",
                              },
                              gap: 2,
                            }}
                          >
                            {obs.map((o: any, index: number) => (
                              <Paper
                                key={index}
                                elevation={0}
                                sx={{
                                  p: 2.5,
                                  borderRadius: 2,
                                  boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
                                  bgcolor: "#fff",
                                  border: "1px solid",
                                  borderColor: "rgba(0,0,0,0.05)",
                                  display: "flex",
                                  flexDirection: "column",
                                  minHeight: 140,
                                }}
                              >
                                <Box display="flex" gap={2}>
                                  <Box
                                    sx={{
                                      width: 32,
                                      height: 32,
                                      borderRadius: "50%",
                                      bgcolor: "rgba(0, 0, 0, 0.04)",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      color: "text.secondary",
                                    }}
                                  >
                                    <MonitorHeartRounded sx={{ fontSize: 18 }} />
                                  </Box>
                                  <Box>
                                    <Typography
                                      variant="body1"
                                      fontWeight={700}
                                      sx={{
                                        mb: 1,
                                        fontSize: "0.9rem",
                                        lineHeight: 1.3,
                                      }}
                                    >
                                      {o.code?.text ||
                                        o.code?.coding?.[0]?.display ||
                                        "Measurement"}
                                    </Typography>
                                  </Box>
                                </Box>
                                <Box sx={{ mt: "auto" }}>
                                  <Typography
                                    variant="h4"
                                    fontWeight={800}
                                    color="primary.main"
                                  >
                                    {o.valueQuantity?.value}{" "}
                                    <Typography
                                      component="span"
                                      variant="h6"
                                      fontWeight={400}
                                      color="text.secondary"
                                    >
                                      {o.valueQuantity?.unit}
                                    </Typography>
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    Effective:{" "}
                                    {o.effectiveDateTime
                                      ? new Date(
                                          o.effectiveDateTime,
                                        ).toLocaleDateString()
                                      : "N/A"}
                                  </Typography>
                                </Box>
                              </Paper>
                            ))}
                          </Box>
                        </Box>
                      ),
                    )
                  ) : (
                    <Typography color="text.secondary">
                      {localSearch
                        ? "No vital signs match your search."
                        : "No vital signs found for this patient."}
                    </Typography>
                  )}
                </Box>
              )}

              {/* Labs & Results Section */}
              {activeSection === "labs" && (
                <Box id="labs">
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "1fr 1fr",
                        md: "1fr 1fr 1fr",
                      },
                      gap: 2,
                    }}
                  >
                    {labObservations.length > 0 ? (
                      labObservations.map((o: any, index: number) => (
                        <Paper
                          key={index}
                          elevation={0}
                          sx={{
                            p: 2.5,
                            borderRadius: 2,
                            boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
                            bgcolor: "#fff",
                            border: "1px solid",
                            borderColor: "rgba(0,0,0,0.05)",
                            display: "flex",
                            flexDirection: "column",
                            minHeight: 140,
                          }}
                        >
                          <Box display="flex" gap={2}>
                            <Box
                              sx={{
                                width: 32,
                                height: 32,
                                borderRadius: "50%",
                                bgcolor: "rgba(0, 0, 0, 0.04)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "text.secondary",
                              }}
                            >
                              <ScienceRounded sx={{ fontSize: 18 }} />
                            </Box>
                            <Box>
                              <Typography
                                variant="subtitle2"
                                color="text.secondary"
                                gutterBottom
                              >
                                {o.category?.[0]?.coding?.[0]?.display ||
                                  "Lab Result"}
                              </Typography>
                              <Typography
                                variant="body1"
                                fontWeight={700}
                                sx={{
                                  mb: 1,
                                  fontSize: "0.9rem",
                                  lineHeight: 1.3,
                                }}
                              >
                                {o.code?.text ||
                                  o.code?.coding?.[0]?.display ||
                                  "Measurement"}
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ mt: "auto" }}>
                            <Typography
                              variant="h4"
                              fontWeight={800}
                              color="primary.main"
                            >
                              {o.valueQuantity?.value}{" "}
                              <Typography
                                component="span"
                                variant="h6"
                                fontWeight={400}
                                color="text.secondary"
                              >
                                {o.valueQuantity?.unit}
                              </Typography>
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Effective:{" "}
                              {o.effectiveDateTime
                                ? new Date(
                                    o.effectiveDateTime,
                                  ).toLocaleDateString()
                                : "N/A"}
                            </Typography>
                          </Box>
                        </Paper>
                      ))
                    ) : (
                      <Typography color="text.secondary">
                        {localSearch
                          ? "No lab results match your search."
                          : "No lab results found for this patient."}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}

              {/* Observations Section */}
              {activeSection === "observations" && (
                <Box id="observations">
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "1fr 1fr",
                        md: "1fr 1fr 1fr",
                      },
                      gap: 2,
                    }}
                  >
                    {filteredObservations.length > 0 ? (
                      filteredObservations.map((o: any, index: number) => (
                        <Paper
                          key={index}
                          elevation={0}
                          sx={{
                            p: 2.5,
                            borderRadius: 2,
                            boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
                            bgcolor: "#fff",
                            border: "1px solid",
                            borderColor: "rgba(0,0,0,0.05)",
                            display: "flex",
                            flexDirection: "column",
                            minHeight: 140,
                          }}
                        >
                          <Box display="flex" gap={2}>
                            <Box
                              sx={{
                                width: 32,
                                height: 32,
                                borderRadius: "50%",
                                bgcolor: "rgba(0, 0, 0, 0.04)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "text.secondary",
                              }}
                            >
                              <MonitorHeartRounded sx={{ fontSize: 18 }} />
                            </Box>
                            <Box>
                              <Typography
                                variant="subtitle2"
                                color="text.secondary"
                                gutterBottom
                              >
                                {o.category?.[0]?.coding?.[0]?.display ||
                                  "Observation"}
                              </Typography>
                              <Typography
                                variant="body1"
                                fontWeight={700}
                                sx={{
                                  mb: 1,
                                  fontSize: "0.9rem",
                                  lineHeight: 1.3,
                                }}
                              >
                                {o.code?.text ||
                                  o.code?.coding?.[0]?.display ||
                                  "Measurement"}
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ mt: "auto" }}>
                            <Typography
                              variant="h4"
                              fontWeight={800}
                              color="primary.main"
                            >
                              {o.valueQuantity?.value}{" "}
                              <Typography
                                component="span"
                                variant="h6"
                                fontWeight={400}
                                color="text.secondary"
                              >
                                {o.valueQuantity?.unit}
                              </Typography>
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Effective:{" "}
                              {o.effectiveDateTime
                                ? new Date(
                                    o.effectiveDateTime,
                                  ).toLocaleDateString()
                                : "N/A"}
                            </Typography>
                          </Box>
                        </Paper>
                      ))
                    ) : (
                      <Typography color="text.secondary">
                        {localSearch
                          ? "No observations match your search."
                          : "No observations found for this patient."}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}

              {/* Medications Section */}
              {activeSection === "medications" && (
                <Box id="medications">
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                    }}
                  >
                    {filteredMedications.length > 0 ? (
                      filteredMedications.map((m: any, index: number) => (
                        <Paper
                          key={m.id || index}
                          elevation={0}
                          sx={{
                            p: 2.5,
                            borderRadius: 2,
                            boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
                            bgcolor: "#fff",
                            border: "1px solid",
                            borderColor: "rgba(0,0,0,0.05)",
                            transition: "all 0.2s",
                            "&:hover": { transform: "translateY(-2px)" },
                          }}
                        >
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="flex-start"
                          >
                            <Box display="flex" gap={2} sx={{ flex: 1 }}>
                              <Box
                                sx={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: "50%",
                                  bgcolor: "rgba(0, 0, 0, 0.04)",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  color: "text.secondary",
                                }}
                              >
                                <MedicationRounded sx={{ fontSize: 18 }} />
                              </Box>
                              <Box sx={{ flex: 1 }}>
                                <Typography
                                  variant="body1"
                                  fontWeight={700}
                                  color="text.primary"
                                  sx={{ fontSize: "0.95rem" }}
                                  gutterBottom
                                >
                                  {getMedicationName(m)}
                                </Typography>

                                {m.dosageInstruction?.[0]?.text && (
                                  <Typography
                                    variant="body1"
                                    fontWeight={500}
                                    sx={{
                                      mb: 2,
                                      p: 1.5,
                                      bgcolor: "rgba(0,0,0,0.03)",
                                      borderRadius: 1,
                                      borderLeft: "4px solid",
                                      borderColor: "primary.main",
                                    }}
                                  >
                                    {m.dosageInstruction[0].text}
                                  </Typography>
                                )}

                                <Box
                                  sx={{
                                    display: "grid",
                                    gridTemplateColumns:
                                      "repeat(auto-fit, minmax(200px, 1fr))",
                                    gap: 2,
                                  }}
                                >
                                  <Box>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      display="block"
                                    >
                                      Status & Intent
                                    </Typography>
                                    <Stack
                                      direction="row"
                                      spacing={1}
                                      sx={{ mt: 0.5 }}
                                    >
                                      <Chip
                                        label={m.status}
                                        size="small"
                                        sx={{
                                          fontWeight: 700,
                                          fontSize: "0.65rem",
                                          bgcolor:
                                            m.status === "active"
                                              ? "rgba(16, 185, 129, 0.08)"
                                              : "rgba(0,0,0,0.04)",
                                          color:
                                            m.status === "active"
                                              ? "success.main"
                                              : "text.secondary",
                                          border: "none",
                                          borderRadius: "4px",
                                          textTransform: "capitalize",
                                        }}
                                      />
                                      <Chip
                                        label={m.intent}
                                        size="small"
                                        sx={{
                                          fontWeight: 700,
                                          fontSize: "0.65rem",
                                          bgcolor: "rgba(0, 128, 240, 0.08)",
                                          color: "primary.main",
                                          border: "none",
                                          borderRadius: "4px",
                                          textTransform: "capitalize",
                                        }}
                                      />
                                    </Stack>
                                  </Box>

                                  {m.requester && (
                                    <Box>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        display="block"
                                      >
                                        Prescribed By
                                      </Typography>
                                      {(() => {
                                        const pract = getPractitionerDetails(
                                          m.requester.reference,
                                        );
                                        if (pract) {
                                          return (
                                            <>
                                              <Typography
                                                variant="body2"
                                                fontWeight={600}
                                              >
                                                {pract.fullName}
                                              </Typography>
                                              <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{
                                                  display: "block",
                                                  mt: 0.5,
                                                }}
                                              >
                                                {pract.telecom}
                                              </Typography>
                                            </>
                                          );
                                        }
                                        return (
                                          <Typography
                                            variant="body2"
                                            fontWeight={600}
                                          >
                                            {m.requester.display ||
                                              m.requester.reference ||
                                              "N/A"}
                                          </Typography>
                                        );
                                      })()}
                                    </Box>
                                  )}

                                  {m.authoredOn && (
                                    <Box>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        display="block"
                                      >
                                        Authored On
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        fontWeight={600}
                                      >
                                        {new Date(
                                          m.authoredOn,
                                        ).toLocaleDateString(undefined, {
                                          dateStyle: "long",
                                        })}
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                              </Box>
                              <IconButton color="primary" size="small" disabled>
                                <MedicationRounded />
                              </IconButton>
                            </Box>
                          </Box>
                        </Paper>
                      ))
                    ) : (
                      <Typography color="text.secondary">
                        {localSearch
                          ? "No medications match your search."
                          : "No medication records found."}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}

              {/* Allergies Section */}
              {activeSection === "allergies" && (
                <Box id="allergies">
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                    }}
                  >
                    {filteredAllergies.length > 0 ? (
                      filteredAllergies.map((a: any, index: number) => (
                        <Paper
                          key={index}
                          elevation={0}
                          sx={{
                            p: 2.5,
                            borderRadius: 2,
                            boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
                            bgcolor: "#fff",
                            border: "1px solid",
                            borderColor: "rgba(0,0,0,0.05)",
                          }}
                        >
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="flex-start"
                          >
                            <Box display="flex" gap={2}>
                              <Box
                                sx={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: "50%",
                                  bgcolor: "rgba(0, 0, 0, 0.04)",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  color: "text.secondary",
                                }}
                              >
                                <CoronavirusRounded sx={{ fontSize: 18 }} />
                              </Box>
                              <Box>
                                <Typography
                                  variant="body1"
                                  fontWeight={700}
                                  color="text.primary"
                                  sx={{ fontSize: "0.95rem" }}
                                >
                                  {a.code?.text ||
                                    a.code?.coding?.[0]?.display ||
                                    "Allergy"}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  fontWeight={500}
                                  sx={{ mt: 1 }}
                                >
                                  Status:{" "}
                                  <Box
                                    component="span"
                                    sx={{ fontWeight: 700 }}
                                  >
                                    {a.clinicalStatus?.coding?.[0]?.code ||
                                      "N/A"}
                                  </Box>{" "}
                                  | Criticality:{" "}
                                  <Box
                                    component="span"
                                    sx={{ fontWeight: 700 }}
                                  >
                                    {a.criticality || "N/A"}
                                  </Box>
                                </Typography>
                              </Box>
                            </Box>
                            <Chip
                              label={a.category?.[0] || "Allergy"}
                              size="small"
                              sx={{
                                fontWeight: 700,
                                fontSize: "0.65rem",
                                bgcolor: "rgba(239, 68, 68, 0.08)",
                                color: "error.main",
                                border: "none",
                                borderRadius: "4px",
                              }}
                            />
                          </Box>
                        </Paper>
                      ))
                    ) : (
                      <Typography color="text.secondary">
                        {localSearch
                          ? "No allergies match your search."
                          : "No allergy records found."}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}

              {/* Procedures Section */}
              {activeSection === "procedures" && (
                <Box id="procedures">
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                    }}
                  >
                    {filteredProcedures.length > 0 ? (
                      filteredProcedures.map((p: any, index: number) => (
                        <Paper
                          key={index}
                          elevation={0}
                          onClick={() => handleOpenProcedure(p)}
                          sx={{
                            p: 2.5,
                            borderRadius: 2,
                            boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
                            bgcolor: "#fff",
                            border: "1px solid",
                            borderColor: "rgba(0,0,0,0.05)",
                            cursor: "pointer",
                            "&:hover": {
                              borderColor: "rgba(0, 128, 240, 0.3)",
                            },
                          }}
                        >
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="flex-start"
                          >
                            <Box display="flex" gap={2}>
                              <Box
                                sx={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: "50%",
                                  bgcolor: "rgba(0, 0, 0, 0.04)",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  color: "text.secondary",
                                }}
                              >
                                <MedicalServicesRounded sx={{ fontSize: 18 }} />
                              </Box>
                              <Box>
                                <Typography
                                  variant="body1"
                                  fontWeight={700}
                                  sx={{ fontSize: "0.95rem" }}
                                >
                                  {p.code?.text ||
                                    p.code?.coding?.[0]?.display ||
                                    p.code?.extension?.[0]?.valueCodeableConcept
                                      ?.coding?.[0]?.display ||
                                    "Procedure"}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  fontWeight={500}
                                  sx={{ mt: 1 }}
                                >
                                  Status:{" "}
                                  <Box
                                    component="span"
                                    sx={{ fontWeight: 700 }}
                                  >
                                    {p.status}
                                  </Box>
                                </Typography>
                                {p.performedDateTime && (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    fontWeight={500}
                                  >
                                    Performed on:{" "}
                                    <Box
                                      component="span"
                                      sx={{ fontWeight: 700 }}
                                    >
                                      {new Date(
                                        p.performedDateTime,
                                      ).toLocaleDateString()}
                                    </Box>
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                            <Chip
                              label="Procedure"
                              size="small"
                              sx={{
                                fontWeight: 700,
                                fontSize: "0.65rem",
                                bgcolor: "rgba(0, 128, 240, 0.08)",
                                color: "primary.main",
                                border: "none",
                                borderRadius: "4px",
                              }}
                            />
                          </Box>
                        </Paper>
                      ))
                    ) : (
                      <Typography color="text.secondary">
                        {localSearch
                          ? "No procedures match your search."
                          : "No procedures found."}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}

              {/* Surgical History Section */}
              {activeSection === "surgical-history" && (
                <Box id="surgical-history">
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                    }}
                  >
                    {surgicalProcedures.length > 0 ? (
                      surgicalProcedures.map((p: any, index: number) => (
                        <Paper
                          key={index}
                          elevation={0}
                          onClick={() => handleOpenProcedure(p)}
                          sx={{
                            p: 2.5,
                            borderRadius: 2,
                            boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
                            bgcolor: "#fff",
                            border: "1px solid",
                            borderColor: "rgba(0,0,0,0.05)",
                            cursor: "pointer",
                            "&:hover": {
                              borderColor: "rgba(0, 128, 240, 0.3)",
                            },
                          }}
                        >
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="flex-start"
                          >
                            <Box display="flex" gap={2}>
                              <Box
                                sx={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: "50%",
                                  bgcolor: "rgba(0, 0, 0, 0.04)",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  color: "text.secondary",
                                }}
                              >
                                <LocalHospitalRounded sx={{ fontSize: 18 }} />
                              </Box>
                              <Box>
                                <Typography
                                  variant="body1"
                                  fontWeight={700}
                                  sx={{ fontSize: "0.95rem" }}
                                >
                                  {p.code?.text ||
                                    p.code?.coding?.[0]?.display ||
                                    "Procedure"}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  fontWeight={500}
                                  sx={{ mt: 1 }}
                                >
                                  Status:{" "}
                                  <Box
                                    component="span"
                                    sx={{ fontWeight: 700 }}
                                  >
                                    {p.status}
                                  </Box>
                                </Typography>
                                {p.performedDateTime && (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    fontWeight={500}
                                  >
                                    Performed on:{" "}
                                    <Box
                                      component="span"
                                      sx={{ fontWeight: 700 }}
                                    >
                                      {new Date(
                                        p.performedDateTime,
                                      ).toLocaleDateString()}
                                    </Box>
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                            <Chip
                              label={
                                p.code?.coding?.find((c: any) => c.code)
                                  ?.code
                                  ? `CPT ${p.code.coding.find((c: any) => c.code).code}`
                                  : "Surgical"
                              }
                              size="small"
                              sx={{
                                fontWeight: 700,
                                fontSize: "0.65rem",
                                bgcolor: "rgba(0, 128, 240, 0.08)",
                                color: "primary.main",
                                border: "none",
                                borderRadius: "4px",
                              }}
                            />
                          </Box>
                        </Paper>
                      ))
                    ) : (
                      <Typography color="text.secondary">
                        {localSearch
                          ? "No surgical history matches your search."
                          : "No surgical history found."}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}

              {/* Care Plans Section */}
              {activeSection === "careplans" && (
                <Box id="careplans">
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                    }}
                  >
                    {filteredCarePlans.length > 0 ? (
                      filteredCarePlans.map((cp: any, index: number) => (
                        <Paper
                          key={index}
                          elevation={0}
                          sx={{
                            p: 2.5,
                            borderRadius: 2,
                            boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
                            bgcolor: "#fff",
                            border: "1px solid",
                            borderColor: "rgba(0,0,0,0.05)",
                          }}
                        >
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="flex-start"
                          >
                            <Box display="flex" gap={2}>
                              <Box
                                sx={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: "50%",
                                  bgcolor: "rgba(0, 0, 0, 0.04)",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  color: "text.secondary",
                                }}
                              >
                                <FactCheckRounded sx={{ fontSize: 18 }} />
                              </Box>
                              <Box>
                                <Typography
                                  variant="body1"
                                  fontWeight={700}
                                  sx={{ fontSize: "0.95rem" }}
                                >
                                  {cp.category?.[0]?.coding?.[0]?.display ||
                                    "Care Plan"}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  fontWeight={500}
                                  sx={{ mt: 1 }}
                                >
                                  Status:{" "}
                                  <Box
                                    component="span"
                                    sx={{ fontWeight: 700 }}
                                  >
                                    {cp.status}
                                  </Box>{" "}
                                  | Intent:{" "}
                                  <Box
                                    component="span"
                                    sx={{ fontWeight: 700 }}
                                  >
                                    {cp.intent}
                                  </Box>
                                </Typography>
                                <Typography
                                  variant="body2"
                                  fontWeight={500}
                                  sx={{ mt: 1, color: "text.primary" }}
                                >
                                  {cp.text?.div ? (
                                    <div
                                      dangerouslySetInnerHTML={{
                                        __html: cp.text.div,
                                      }}
                                    />
                                  ) : (
                                    "No summary provided."
                                  )}
                                </Typography>
                              </Box>
                            </Box>
                            <Chip
                              label="Care Plan"
                              size="small"
                              sx={{
                                fontWeight: 700,
                                fontSize: "0.65rem",
                                bgcolor: "rgba(16, 185, 129, 0.08)",
                                color: "success.main",
                                border: "none",
                                borderRadius: "4px",
                              }}
                            />
                          </Box>
                        </Paper>
                      ))
                    ) : (
                      <Typography color="text.secondary">
                        {localSearch
                          ? "No care plans match your search."
                          : "No care plans found."}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}

              {/* Immunizations Section */}
              {activeSection === "immunizations" && (
                <Box id="immunizations">
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                    }}
                  >
                    {filteredImmunizations.length > 0 ? (
                      filteredImmunizations.map((i: any, index: number) => (
                        <Paper
                          key={index}
                          elevation={0}
                          sx={{
                            p: 2.5,
                            borderRadius: 2,
                            boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
                            bgcolor: "#fff",
                            border: "1px solid",
                            borderColor: "rgba(0,0,0,0.05)",
                          }}
                        >
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="flex-start"
                          >
                            <Box display="flex" gap={2}>
                              <Box
                                sx={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: "50%",
                                  bgcolor: "rgba(0, 0, 0, 0.04)",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  color: "text.secondary",
                                }}
                              >
                                <VaccinesRounded sx={{ fontSize: 18 }} />
                              </Box>
                              <Box>
                                <Typography
                                  variant="body1"
                                  fontWeight={700}
                                  sx={{ fontSize: "0.95rem" }}
                                >
                                  {i.vaccineCode?.text ||
                                    i.vaccineCode?.coding?.[0]?.display ||
                                    "Vaccination"}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  fontWeight={500}
                                  sx={{ mt: 1 }}
                                >
                                  Status:{" "}
                                  <Box
                                    component="span"
                                    sx={{ fontWeight: 700 }}
                                  >
                                    {i.status}
                                  </Box>
                                </Typography>
                                {i.occurrenceDateTime && (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    fontWeight={500}
                                  >
                                    Date:{" "}
                                    <Box
                                      component="span"
                                      sx={{ fontWeight: 700 }}
                                    >
                                      {new Date(
                                        i.occurrenceDateTime,
                                      ).toLocaleDateString()}
                                    </Box>
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                            <Chip
                              label="Immunization"
                              size="small"
                              sx={{
                                fontWeight: 700,
                                fontSize: "0.65rem",
                                bgcolor: "rgba(0, 128, 240, 0.08)",
                                color: "primary.main",
                                border: "none",
                                borderRadius: "4px",
                              }}
                            />
                          </Box>
                        </Paper>
                      ))
                    ) : (
                      <Typography color="text.secondary">
                        {localSearch
                          ? "No immunizations match your search."
                          : "No immunizations recorded."}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}

              {/* Diagnostic Reports Section */}
              {activeSection === "reports" && (
                <Box id="reports">
                  <Stack spacing={2}>
                    {filteredReports.length > 0 ? (
                      filteredReports.map((dr: any, index: number) => (
                        <Paper
                          key={index}
                          elevation={0}
                          sx={{
                            p: 2.5,
                            borderRadius: 2,
                            boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
                            bgcolor: "#fff",
                            border: "1px solid",
                            borderColor: "rgba(0,0,0,0.05)",
                          }}
                        >
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="flex-start"
                          >
                            <Box display="flex" gap={2}>
                              <Box
                                sx={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: "50%",
                                  bgcolor: "rgba(0, 0, 0, 0.04)",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  color: "text.secondary",
                                }}
                              >
                                <AssessmentRounded sx={{ fontSize: 18 }} />
                              </Box>
                              <Box>
                                <Typography
                                  variant="body1"
                                  fontWeight={700}
                                  sx={{ fontSize: "0.95rem" }}
                                >
                                  {dr.code?.text ||
                                    dr.code?.coding?.[0]?.display ||
                                    "Diagnostic Report"}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  fontWeight={500}
                                  sx={{ mt: 1 }}
                                >
                                  Status:{" "}
                                  <Box
                                    component="span"
                                    sx={{ fontWeight: 700 }}
                                  >
                                    {dr.status}
                                  </Box>{" "}
                                  | Category:{" "}
                                  <Box
                                    component="span"
                                    sx={{ fontWeight: 700 }}
                                  >
                                    {dr.category?.[0]?.coding?.[0]?.display ||
                                      "N/A"}
                                  </Box>
                                </Typography>
                                {dr.effectiveDateTime && (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    fontWeight={500}
                                  >
                                    Date:{" "}
                                    <Box
                                      component="span"
                                      sx={{ fontWeight: 700 }}
                                    >
                                      {new Date(
                                        dr.effectiveDateTime,
                                      ).toLocaleDateString()}
                                    </Box>
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                            <Chip
                              label="Report"
                              size="small"
                              sx={{
                                fontWeight: 700,
                                fontSize: "0.65rem",
                                bgcolor: "rgba(0, 128, 240, 0.08)",
                                color: "primary.main",
                                border: "none",
                                borderRadius: "4px",
                              }}
                            />
                          </Box>

                          <Box sx={{ mt: 3 }}>
                            <Typography
                              variant="subtitle2"
                              color="primary"
                              fontWeight={700}
                              gutterBottom
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <MonitorHeartRounded fontSize="small" />{" "}
                              Observations
                            </Typography>
                            <Divider sx={{ mb: 2 }} />

                            {dr.result && dr.result.length > 0 ? (
                              <Box
                                sx={{
                                  display: "grid",
                                  gridTemplateColumns:
                                    "repeat(auto-fit, minmax(300px, 1fr))",
                                  gap: 2,
                                }}
                              >
                                {dr.result.map((res: any, rIdx: number) => {
                                  const obsId = res.reference?.split("/").pop();
                                  const obs = Observation.find(
                                    (o: any) => o.id === obsId,
                                  );

                                  if (!obs) return null;

                                  return (
                                    <Paper
                                      key={rIdx}
                                      elevation={0}
                                      sx={{
                                        p: 2,
                                        borderRadius: 1,
                                        bgcolor: "rgba(0,0,0,0.01)",
                                        border: "1px solid",
                                        borderColor: "divider",
                                      }}
                                    >
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        display="block"
                                      >
                                        {obs.category?.[0]?.coding?.[0]
                                          ?.display || "Observation"}
                                      </Typography>
                                      <Typography
                                        variant="subtitle1"
                                        fontWeight={700}
                                      >
                                        {obs.code?.text ||
                                          obs.code?.coding?.[0]?.display ||
                                          "Measurement"}
                                      </Typography>
                                      <Typography
                                        variant="h5"
                                        fontWeight={800}
                                        color="primary.main"
                                        sx={{ mt: 1 }}
                                      >
                                        {obs.valueQuantity?.value}{" "}
                                        <Typography
                                          component="span"
                                          variant="body2"
                                          fontWeight={400}
                                          color="text.secondary"
                                        >
                                          {obs.valueQuantity?.unit}
                                        </Typography>
                                      </Typography>
                                    </Paper>
                                  );
                                })}
                                {/* Filter out nulls and check if anything rendered */}
                                {dr.result.every(
                                  (res: any) =>
                                    !Observation.find(
                                      (o: any) =>
                                        o.id ===
                                        res.reference?.split("/").pop(),
                                    ),
                                ) && (
                                  <Paper
                                    variant="outlined"
                                    sx={{
                                      p: 3,
                                      borderRadius: 1,
                                      textAlign: "center",
                                      bgcolor: "rgba(0,0,0,0.02)",
                                      borderStyle: "dashed",
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      No matching observation records found for
                                      these results.
                                    </Typography>
                                  </Paper>
                                )}
                              </Box>
                            ) : (
                              <Paper
                                variant="outlined"
                                sx={{
                                  p: 3,
                                  borderRadius: 1,
                                  textAlign: "center",
                                  bgcolor: "rgba(255, 0, 0, 0.02)",
                                  borderStyle: "dashed",
                                  borderColor: "error.light",
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  color="error.main"
                                  fontWeight={600}
                                >
                                  No result for this diagnosis
                                </Typography>
                              </Paper>
                            )}
                          </Box>
                        </Paper>
                      ))
                    ) : (
                      <Typography color="text.secondary">
                        {localSearch
                          ? "No diagnostic reports match your search."
                          : "No diagnostic reports found."}
                      </Typography>
                    )}
                  </Stack>
                </Box>
              )}

              {/* Documents Section */}
              {activeSection === "documents" && (
                <Box id="documents">
                  <Table
                    headers={
                      [
                        {
                          key: "Name",
                          label: "Document Name",
                          sortable: true,
                          render: (row: any) => {
                            const typeLabel = row.Type
                              ? row.Type.split("/").pop()?.toUpperCase()
                              : null;
                            return (
                              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                                <Typography variant="body2" fontWeight={600} color="text.primary">
                                  {row.Name || "Unnamed Document"}
                                </Typography>
                                {typeLabel && (
                                  <Box
                                    component="span"
                                    sx={{
                                      display: "inline-block",
                                      fontSize: "0.65rem",
                                      fontWeight: 700,
                                      px: 0.8,
                                      py: 0.2,
                                      borderRadius: "4px",
                                      bgcolor: "rgba(99,102,241,0.1)",
                                      color: "#4f46e5",
                                      width: "fit-content",
                                      textTransform: "uppercase",
                                      letterSpacing: "0.05em",
                                    }}
                                  >
                                    {typeLabel}
                                  </Box>
                                )}
                              </Box>
                            );
                          },
                        },
                        {
                          key: "Date",
                          label: "Date",
                          sortable: true,
                          render: (row: any) =>
                            row.Date
                              ? new Date(row.Date).toLocaleString()
                              : "N/A",
                        },
                        {
                          key: "Docstatus",
                          label: "Status",
                          sortable: true,
                          render: (row: any) => (
                            <StatusDot
                              color={
                                row.Docstatus === "final"
                                  ? "#10b981"
                                  : "#6b7280"
                              }
                              label={row.Docstatus || "final"}
                            />
                          ),
                        },
                        { key: "Type", label: "Type", sortable: true },
                        {
                          key: "actions",
                          label: "Actions",
                          align: "right",
                          render: (row: any) => (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleOpenDoc(row)}
                              sx={{
                                borderRadius: 1,
                                textTransform: "none",
                                fontWeight: 600,
                                fontSize: "0.75rem",
                                py: 0.5,
                              }}
                            >
                              View
                            </Button>
                          ),
                        },
                      ] as TableHeaderConfig[]
                    }
                    data={filteredDocuments}
                    gridTemplateColumns="2.5fr 1.5fr 1fr 1fr 1fr"
                    emptyState={
                      localSearch
                        ? "No documents match your search."
                        : "No documents found for this patient."
                    }
                    variant="cards"
                  />
                </Box>
              )}

              {/* Advanced Directives Section */}
              {activeSection === "advanced-directives" && (
                <Box id="advanced-directives">
                  <Table
                    headers={
                      [
                        { key: "Name", label: "Document Name", sortable: true },
                        {
                          key: "Date",
                          label: "Date",
                          sortable: true,
                          render: (row: any) =>
                            row.Date
                              ? new Date(row.Date).toLocaleString()
                              : "N/A",
                        },
                        {
                          key: "Docstatus",
                          label: "Status",
                          sortable: true,
                          render: (row: any) => (
                            <StatusDot
                              color={
                                row.Docstatus === "final"
                                  ? "#10b981"
                                  : "#6b7280"
                              }
                              label={row.Docstatus || "final"}
                            />
                          ),
                        },
                        { key: "Type", label: "Type", sortable: true },
                        {
                          key: "actions",
                          label: "Actions",
                          align: "right",
                          render: (row: any) => (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleOpenDoc(row)}
                              sx={{
                                borderRadius: 1,
                                textTransform: "none",
                                fontWeight: 600,
                                fontSize: "0.75rem",
                                py: 0.5,
                              }}
                            >
                              View
                            </Button>
                          ),
                        },
                      ] as TableHeaderConfig[]
                    }
                    data={filteredAdvancedDirectives}
                    gridTemplateColumns="2.5fr 1.5fr 1fr 1fr 1fr"
                    emptyState="No advanced directives found for this patient."
                    variant="cards"
                  />
                </Box>
              )}

              {/* Code Status Section */}
              {activeSection === "code-status" && (
                <Box id="code-status">
                  <Alert severity="warning" sx={{ mb: 2, borderRadius: 1 }}>
                    Detected from documents and care plans by keyword match —
                    not a structured PCC field. Always verify the patient's
                    actual code status in PCC.
                  </Alert>
                  {codeStatusDetection.sources.length > 0 ? (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                      {codeStatusDetection.sources.map((s, i) => (
                        <Box
                          key={i}
                          onClick={() => {
                            const idx = tabs.findIndex((t) => t.id === s.tab);
                            if (idx !== -1) {
                              isProgrammaticNav.current = true;
                              setActiveTab(idx);
                            }
                            if (s.tab === "documents") {
                              setSelectedDoc(documents[s.index]);
                              setIsDocOpen(true);
                            }
                          }}
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 1,
                            bgcolor: "rgba(220, 38, 38, 0.06)",
                            border: "1px solid rgba(220, 38, 38, 0.15)",
                            borderRadius: 1,
                            px: 2,
                            py: 1.5,
                            cursor: "pointer",
                            "&:hover": { bgcolor: "rgba(220, 38, 38, 0.12)", borderColor: "#dc2626" },
                          }}
                        >
                          <InfoOutlined sx={{ fontSize: 16, color: "#dc2626", mt: "2px", flexShrink: 0 }} />
                          <Box>
                            <Typography variant="body2" fontWeight={500} color="#dc2626">
                              {s.label}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Matched term: "{s.matchedTerm}"
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Typography color="text.secondary">
                      No code status or advance directive keywords detected for this patient.
                    </Typography>
                  )}
                </Box>
              )}

              {/* Evaluations Section */}
              {activeSection === "evaluations" && (
                <Box id="evaluations">
                  <Table
                    headers={
                      [
                        { key: "Name", label: "Document Name", sortable: true },
                        {
                          key: "Date",
                          label: "Date",
                          sortable: true,
                          render: (row: any) =>
                            row.Date
                              ? new Date(row.Date).toLocaleString()
                              : "N/A",
                        },
                        {
                          key: "Docstatus",
                          label: "Status",
                          sortable: true,
                          render: (row: any) => (
                            <StatusDot
                              color={
                                row.Docstatus === "final"
                                  ? "#10b981"
                                  : "#6b7280"
                              }
                              label={row.Docstatus || "final"}
                            />
                          ),
                        },
                        { key: "Type", label: "Type", sortable: true },
                        {
                          key: "actions",
                          label: "Actions",
                          align: "right",
                          render: (row: any) => (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleOpenDoc(row)}
                              sx={{
                                borderRadius: 1,
                                textTransform: "none",
                                fontWeight: 600,
                                fontSize: "0.75rem",
                                py: 0.5,
                              }}
                            >
                              View
                            </Button>
                          ),
                        },
                      ] as TableHeaderConfig[]
                    }
                    data={filteredEvaluations}
                    gridTemplateColumns="2.5fr 1.5fr 1fr 1fr 1fr"
                    emptyState="No evaluations found for this patient."
                    variant="cards"
                  />
                </Box>
              )}
            </Stack>
          </Paper>
        </Box>

        <Dialog
          open={hospiceDialogOpen}
          onClose={() => setHospiceDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 2, p: 1 } }}
        >
          <DialogTitle
            sx={{
              fontWeight: 800,
              fontSize: "1.1rem",
              display: "flex",
              alignItems: "center",
              gap: 1,
              color: "#7c3aed",
              px: 3,
              py: 2,
            }}
          >
            <InfoOutlined sx={{ fontSize: 20 }} />
            Hospice Detected
          </DialogTitle>
          <DialogContent dividers sx={{ px: 3, py: 2 }}>
            <Typography variant="body2" color="text.secondary" mb={2}>
              The keyword <strong>"hospice"</strong> was found in the following records for this patient:
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {hospiceDetection.sources.map((s, i) => (
                <Box
                  key={i}
                  onClick={() => {
                    const idx = tabs.findIndex((t) => t.id === s.tab);
                    if (idx !== -1) {
                      isProgrammaticNav.current = true;
                      setActiveTab(idx);
                    }
                    if (s.tab === "documents") {
                      setSelectedDoc(documents[s.index]);
                      setIsDocOpen(true);
                    }
                    setHospiceDialogOpen(false);
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1,
                    bgcolor: "rgba(124, 58, 237, 0.06)",
                    border: "1px solid rgba(124, 58, 237, 0.15)",
                    borderRadius: 1,
                    px: 2,
                    py: 1,
                    cursor: "pointer",
                    "&:hover": { bgcolor: "rgba(124, 58, 237, 0.12)", borderColor: "#7c3aed" },
                  }}
                >
                  <InfoOutlined sx={{ fontSize: 16, color: "#7c3aed", mt: "2px", flexShrink: 0 }} />
                  <Typography variant="body2" fontWeight={500} color="#7c3aed">
                    {s.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button
              onClick={() => setHospiceDialogOpen(false)}
              variant="outlined"
              sx={{ borderRadius: 1, textTransform: "none", fontWeight: 600, borderColor: "#7c3aed", color: "#7c3aed", "&:hover": { borderColor: "#6d28d9", bgcolor: "rgba(124,58,237,0.05)" } }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={codeStatusDialogOpen}
          onClose={() => setCodeStatusDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 2, p: 1 } }}
        >
          <DialogTitle
            sx={{
              fontWeight: 800,
              fontSize: "1.1rem",
              display: "flex",
              alignItems: "center",
              gap: 1,
              color: "#dc2626",
              px: 3,
              py: 2,
            }}
          >
            <InfoOutlined sx={{ fontSize: 20 }} />
            Advance Directive / Code Status Keywords Detected
          </DialogTitle>
          <DialogContent dividers sx={{ px: 3, py: 2 }}>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Detected from documents — verify in PCC. The following records
              contain a code-status related term:
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {codeStatusDetection.sources.map((s, i) => (
                <Box
                  key={i}
                  onClick={() => {
                    const idx = tabs.findIndex((t) => t.id === s.tab);
                    if (idx !== -1) {
                      isProgrammaticNav.current = true;
                      setActiveTab(idx);
                    }
                    if (s.tab === "documents") {
                      setSelectedDoc(documents[s.index]);
                      setIsDocOpen(true);
                    }
                    setCodeStatusDialogOpen(false);
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1,
                    bgcolor: "rgba(220, 38, 38, 0.06)",
                    border: "1px solid rgba(220, 38, 38, 0.15)",
                    borderRadius: 1,
                    px: 2,
                    py: 1,
                    cursor: "pointer",
                    "&:hover": { bgcolor: "rgba(220, 38, 38, 0.12)", borderColor: "#dc2626" },
                  }}
                >
                  <InfoOutlined sx={{ fontSize: 16, color: "#dc2626", mt: "2px", flexShrink: 0 }} />
                  <Box>
                    <Typography variant="body2" fontWeight={500} color="#dc2626">
                      {s.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Matched term: "{s.matchedTerm}"
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button
              onClick={() => setCodeStatusDialogOpen(false)}
              variant="outlined"
              sx={{ borderRadius: 1, textTransform: "none", fontWeight: 600, borderColor: "#dc2626", color: "#dc2626", "&:hover": { borderColor: "#b91c1c", bgcolor: "rgba(220,38,38,0.05)" } }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={isProcedureOpen}
          onClose={() => setIsProcedureOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 1, p: 1 } }}
        >
          <DialogTitle sx={{ fontWeight: 800, fontSize: "1.1rem", px: 3, py: 2 }}>
            {selectedProcedure?.code?.text ||
              selectedProcedure?.code?.coding?.[0]?.display ||
              "Procedure"}
          </DialogTitle>
          <DialogContent dividers sx={{ px: 3, py: 2 }}>
            {selectedProcedure && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Status</Typography>
                  <Typography variant="body2" fontWeight={700}>{selectedProcedure.status || "N/A"}</Typography>
                </Box>
                {selectedProcedure.performedDateTime && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Performed On</Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {new Date(selectedProcedure.performedDateTime).toLocaleDateString()}
                    </Typography>
                  </Box>
                )}
                {(selectedProcedure.code?.coding || []).length > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Codes</Typography>
                    <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 0.5 }}>
                      {selectedProcedure.code.coding.map((c: any, i: number) => (
                        <Chip
                          key={i}
                          label={`${c.system?.split("/").pop() || "code"}: ${c.code}${c.display ? ` — ${c.display}` : ""}`}
                          size="small"
                          sx={{ fontWeight: 700, fontSize: "0.65rem", bgcolor: "rgba(0,128,240,0.08)", color: "primary.main", border: "none", borderRadius: "4px" }}
                        />
                      ))}
                    </Stack>
                  </Box>
                )}
                {(selectedProcedure.reasonCode || []).length > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Reason</Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {selectedProcedure.reasonCode
                        .map((r: any) => r.text || r.coding?.[0]?.display)
                        .filter(Boolean)
                        .join(", ") || "N/A"}
                    </Typography>
                  </Box>
                )}
                {selectedProcedure.performer?.[0]?.actor?.reference && (() => {
                  const perf = getPractitionerDetails(selectedProcedure.performer[0].actor.reference);
                  return perf ? (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Performer</Typography>
                      <Typography variant="body2" fontWeight={700}>{perf.fullName}</Typography>
                      {perf.telecom && (
                        <Typography variant="caption" color="text.secondary">{perf.telecom}</Typography>
                      )}
                    </Box>
                  ) : null;
                })()}
                {(selectedProcedure.note || []).length > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Notes</Typography>
                    {selectedProcedure.note.map((n: any, i: number) => (
                      <Typography key={i} variant="body2">{n.text}</Typography>
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={() => setIsProcedureOpen(false)} variant="outlined" sx={{ borderRadius: 1, textTransform: "none", fontWeight: 600 }}>
              Close
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={isDocOpen}
          onClose={() => setIsDocOpen(false)}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 1, p: 1, maxHeight: "90vh" },
          }}
        >
          <DialogTitle
            sx={{
              fontWeight: 800,
              fontSize: "1.25rem",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              color: "text.primary",
              px: 3,
              py: 2,
            }}
          >
            {selectedDoc?.Name || "Document Viewer"}
            <Chip label={selectedDoc?.Docstatus} color="success" size="small" />
          </DialogTitle>
          <DialogContent dividers sx={{ p: 0, overflowY: "auto" }}>
            {selectedDoc &&
              filteredEvaluations.includes(selectedDoc) &&
              (() => {
                const fields = extractWoundFields(selectedDoc.Content);
                const hasFields = Object.keys(fields).length > 0;
                return hasFields ? (
                  <Paper
                    variant="outlined"
                    sx={{
                      m: 2,
                      p: 2,
                      borderRadius: 1,
                      bgcolor: "rgba(0,128,240,0.03)",
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight={800} gutterBottom>
                      Wound Summary
                    </Typography>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 1,
                      }}
                    >
                      {WOUND_FIELD_LABELS.filter((f) => fields[f.key]).map(
                        (f) => (
                          <Box key={f.key}>
                            <Typography variant="caption" color="text.secondary">
                              {f.label}
                            </Typography>
                            <Typography variant="body2" fontWeight={600}>
                              {fields[f.key]}
                            </Typography>
                          </Box>
                        ),
                      )}
                    </Box>
                  </Paper>
                ) : null;
              })()}
            {selectedDoc && renderDocContent(selectedDoc.Content)}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={() => setIsDocOpen(false)}
              variant="outlined"
              sx={{ borderRadius: 1 }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default PatientDetail;
